﻿import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/* ──────────────────────────────────────────────────
   BacktestDemoChart
   Iframe + lightweight-charts@4 (CDN),
   même approche que InteractiveTradingViewChartBacktest.
   Bougies EURUSD H1 hardcodées – aucun appel API.
────────────────────────────────────────────────── */

/**
 * Le contenu de l'iframe est un document autonome : les libellés visibles y sont
 * des jetons `__NOM__` remplacés au rendu par la traduction courante (cf.
 * `landing.backtest.demo.*`). Pas de i18next à l'intérieur de l'iframe.
 */
const DEMO_HTML = `<!DOCTYPE html>
<html lang="__LANG__">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<script src="https://unpkg.com/lightweight-charts@4.2.3/dist/lightweight-charts.standalone.production.js" integrity="sha384-stKllnUqA9AD0gsKCuUtf5XlqAW7PwIgDagoNsTWkjkBmJ/GZ/uHTgEBxdLV2VSK" crossorigin="anonymous"><\/script>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:100%;height:100%;background:#131722;overflow:hidden;
            font-family:-apple-system,BlinkMacSystemFont,'Trebuchet MS',Roboto,sans-serif}

  #topControls{
    position:absolute;top:14px;left:14px;
    display:flex;align-items:center;gap:8px;
    z-index:30;user-select:none;
  }
  #symbolLabel{font-size:14px;font-weight:700;color:#d1d4dc;letter-spacing:.01em;padding:0 4px}
  .sep{width:1px;height:16px;background:rgba(255,255,255,0.1);margin:0 2px;flex-shrink:0}
  .dbtn{
    background:transparent;border:1px solid rgba(255,255,255,0.1);color:#b2b5be;
    padding:6px 14px;border-radius:16px;cursor:pointer;font-size:13px;font-weight:500;
    transition:all .2s;outline:none;display:flex;align-items:center;gap:6px;
  }
  .dbtn:hover{background:rgba(255,255,255,0.06);color:#d1d4dc}
  .dbtn.on{background:rgba(255,255,255,0.10);color:#d1d4dc}
  .dbtn-icon{padding:6px 10px}

  #chart{position:absolute;top:0;left:0;right:0;bottom:36px}

  #bottom{
    position:absolute;bottom:0;left:0;right:0;height:36px;
    display:flex;align-items:center;padding:0 14px;gap:20px;
    background:#1e222d;border-top:1px solid #2a2e39;z-index:30;
  }
  .sl{font-size:11px;color:#787b86;margin-right:3px}
  .sv{font-size:12px;font-weight:700}
  .g{color:#26a69a}.re{color:#ef5350}.w{color:#d1d4dc}

  #rbadge{
    position:absolute;bottom:46px;right:14px;
    background:rgba(19,23,34,.92);border:1px solid #363a45;border-radius:6px;
    padding:5px 10px;font-size:11px;color:#787b86;
    display:flex;align-items:center;gap:6px;pointer-events:none;
    z-index:20;opacity:0;transition:opacity .4s;
  }
  #rbadge.on{opacity:1}
  .dot{width:7px;height:7px;border-radius:50%;background:#26a69a;animation:blink 1s infinite}
  @keyframes blink{0%,100%{opacity:1}50%{opacity:.25}}

  #tpanel{
    position:absolute;top:48px;right:10px;width:168px;
    background:rgba(19,23,34,.96);border:1px solid #2a2e39;border-radius:8px;
    padding:10px 12px;z-index:20;opacity:0;transition:opacity .5s;
  }
  #tpanel.on{opacity:1}
  .tph{font-size:10px;color:#787b86;text-transform:uppercase;letter-spacing:.08em;margin-bottom:8px}
  .tprow{display:flex;justify-content:space-between;align-items:center;margin-bottom:5px}
  .tpk{font-size:11px;color:#787b86}
  .tpv{font-size:12px;font-weight:600;color:#d1d4dc}
  .tppnl{font-size:20px;font-weight:800;color:#26a69a;margin:4px 0 6px;text-align:center}
  .tpd{height:1px;background:#2a2e39;margin:6px 0}
</style>
</head>
<body>
<div id="topControls">
  <span id="symbolLabel">EUR/USD</span>
  <div class="sep"></div>
  <button class="dbtn on" id="tf1">H1</button>
  <button class="dbtn" id="tf4">H4</button>
  <button class="dbtn" id="tfd">1D</button>
  <div class="sep"></div>
  <button class="dbtn">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="16" height="16" fill="none"><path stroke="currentColor" d="M6 12l4.8-4.8a1 1 0 0 1 1.4 0l2.7 2.7a1 1 0 0 0 1.3.1L23 5"></path><path fill="currentColor" fill-rule="evenodd" d="M19 12a1 1 0 0 0-1 1v4h-3v-1a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v2H7a1 1 0 0 0-1 1v4h17V13a1 1 0 0 0-1-1h-3zm0 10h3v-9h-3v9zm-1 0v-4h-3v4h3zm-4-4.5V22h-3v-6h3v1.5zM10 22v-3H7v3h3z"></path></svg>
    __INDICATORS__
  </button>
</div>

<div id="chart"></div>

<div id="rbadge"><span class="dot"></span>&nbsp;__RUNNING__</div>

<div id="tpanel">
  <div class="tph">__RESULT__</div>
  <div class="tppnl" id="tppnl">+$0.00</div>
  <div class="tpd"></div>
  <div class="tprow"><span class="tpk">__PAIR__</span><span class="tpv">EUR/USD</span></div>
  <div class="tprow"><span class="tpk">__DIRECTION__</span><span class="tpv" style="color:#26a69a">&#9650; __LONG__</span></div>
  <div class="tprow"><span class="tpk">__ENTRY__</span><span class="tpv" id="tpentry">—</span></div>
  <div class="tprow"><span class="tpk">__EXIT__</span><span class="tpv" id="tpexit">—</span></div>
  <div class="tprow"><span class="tpk">__PIPS__</span><span class="tpv g" id="tppips">+0</span></div>
  <div class="tprow"><span class="tpk">__LOTS__</span><span class="tpv">0.10</span></div>
</div>

<div id="bottom">
  <div><span class="sl">__SESSION__</span><span class="sv w">__LONDON__</span></div>
  <div><span class="sl">__CANDLES__</span><span class="sv w" id="bc">0</span></div>
  <div><span class="sl">__TRADES__</span><span class="sv w" id="tc">0</span></div>
  <div><span class="sl">__WINRATE__</span><span class="sv g" id="wr">—</span></div>
  <div style="margin-left:auto"><span class="sl">__TOTALPNL__</span><span class="sv g" id="pnl">—</span></div>
</div>

<script>
(function(){
  var CANDLES=[

{time:1769983200,open:1.18492,high:1.1853,low:1.18492,close:1.1853},
{time:1769983260,open:1.18528,high:1.18528,low:1.18505,close:1.18505},
{time:1769983380,open:1.18502,high:1.18503,low:1.18502,close:1.18503},
{time:1769983440,open:1.18502,high:1.18507,low:1.18498,close:1.18499},
{time:1769983500,open:1.18498,high:1.185,low:1.18498,close:1.185},
{time:1769983620,open:1.18502,high:1.18509,low:1.18502,close:1.18509},
{time:1769983740,open:1.18489,high:1.18495,low:1.18489,close:1.18491},
{time:1769983800,open:1.18491,high:1.18509,low:1.18483,close:1.18508},
{time:1769983920,open:1.18484,high:1.18509,low:1.18484,close:1.18509},
{time:1769984100,open:1.18508,high:1.18508,low:1.18479,close:1.1848},
{time:1769984160,open:1.1848,high:1.1849,low:1.18479,close:1.18485},
{time:1769984220,open:1.18487,high:1.18487,low:1.1848,close:1.1848},
{time:1769984280,open:1.1848,high:1.18482,low:1.18479,close:1.18482},
{time:1769984340,open:1.18493,high:1.18493,low:1.18481,close:1.1849},
{time:1769984400,open:1.18491,high:1.18491,low:1.1848,close:1.18483},
{time:1769984460,open:1.18479,high:1.18481,low:1.18476,close:1.18476},
{time:1769984520,open:1.18476,high:1.18476,low:1.18472,close:1.18475},
{time:1769984640,open:1.18479,high:1.18489,low:1.18479,close:1.18489},
{time:1769984700,open:1.18488,high:1.18496,low:1.18475,close:1.18483},
{time:1769984760,open:1.18483,high:1.18486,low:1.1848,close:1.1848},
{time:1769984820,open:1.18482,high:1.1852,low:1.18476,close:1.18515},
{time:1769984880,open:1.18517,high:1.18517,low:1.18497,close:1.185},
{time:1769984940,open:1.18499,high:1.18528,low:1.18495,close:1.18496},
{time:1769985000,open:1.18496,high:1.18506,low:1.18495,close:1.18504},
{time:1769985060,open:1.18506,high:1.18506,low:1.18497,close:1.18497},
{time:1769985120,open:1.18506,high:1.18506,low:1.18495,close:1.18498},
{time:1769985180,open:1.18499,high:1.18504,low:1.18496,close:1.18499},
{time:1769985240,open:1.18499,high:1.18509,low:1.18499,close:1.18507},
{time:1769985300,open:1.18506,high:1.18507,low:1.18505,close:1.18506},
{time:1769985360,open:1.18506,high:1.18508,low:1.18505,close:1.18506},
{time:1769985420,open:1.18507,high:1.18507,low:1.18504,close:1.18505},
{time:1769985480,open:1.18504,high:1.18505,low:1.18503,close:1.18504},
{time:1769985540,open:1.18504,high:1.18506,low:1.18503,close:1.18503},
{time:1769985600,open:1.18503,high:1.18504,low:1.18503,close:1.18503},
{time:1769985840,open:1.18501,high:1.18501,low:1.18498,close:1.18498},
{time:1769985900,open:1.18494,high:1.18496,low:1.18494,close:1.18496},
{time:1769985960,open:1.18496,high:1.18499,low:1.18476,close:1.18478},
{time:1769986020,open:1.18476,high:1.18483,low:1.18475,close:1.18475},
{time:1769986080,open:1.18477,high:1.18482,low:1.18475,close:1.18477},
{time:1769986140,open:1.18484,high:1.18486,low:1.18482,close:1.18486},
{time:1769986200,open:1.18486,high:1.1849,low:1.1848,close:1.18486},
{time:1769986260,open:1.18455,high:1.18487,low:1.18455,close:1.18466},
{time:1769986320,open:1.18469,high:1.18476,low:1.18463,close:1.18464},
{time:1769986380,open:1.18464,high:1.18477,low:1.18461,close:1.18468},
{time:1769986440,open:1.18469,high:1.18474,low:1.18461,close:1.18464},
{time:1769986500,open:1.18468,high:1.18473,low:1.18463,close:1.18471},
{time:1769986560,open:1.18472,high:1.18488,low:1.1845,close:1.18477},
{time:1769986620,open:1.18484,high:1.18487,low:1.18473,close:1.1848},
{time:1769986680,open:1.18481,high:1.18491,low:1.18478,close:1.18489},
{time:1769986740,open:1.18488,high:1.1849,low:1.18487,close:1.18487},
{time:1769986800,open:1.18487,high:1.18493,low:1.18446,close:1.18462},
{time:1769986860,open:1.18462,high:1.18469,low:1.18447,close:1.18468},
{time:1769986920,open:1.18467,high:1.18468,low:1.18467,close:1.18467},
{time:1769986980,open:1.18468,high:1.1847,low:1.18467,close:1.18467},
{time:1769987040,open:1.18467,high:1.1847,low:1.18451,close:1.18453},
{time:1769987100,open:1.18454,high:1.18461,low:1.18447,close:1.18449},
{time:1769987160,open:1.1845,high:1.1845,low:1.18412,close:1.18412},
{time:1769987220,open:1.18412,high:1.18415,low:1.18407,close:1.18414},
{time:1769987280,open:1.18415,high:1.18423,low:1.18409,close:1.18419},
{time:1769987340,open:1.18419,high:1.18419,low:1.18407,close:1.18408},
{time:1769987400,open:1.18408,high:1.1842,low:1.18407,close:1.18416},
{time:1769987460,open:1.18416,high:1.18417,low:1.18401,close:1.18402},
{time:1769987520,open:1.18403,high:1.18453,low:1.18402,close:1.18443},
{time:1769987580,open:1.18446,high:1.18471,low:1.18445,close:1.18458},
{time:1769987640,open:1.1846,high:1.1846,low:1.18406,close:1.1841},
{time:1769987700,open:1.18411,high:1.18418,low:1.18397,close:1.18418},
{time:1769987760,open:1.18419,high:1.18422,low:1.18405,close:1.1841},
{time:1769987820,open:1.18411,high:1.18425,low:1.18403,close:1.18411},
{time:1769987880,open:1.18413,high:1.18438,low:1.18412,close:1.18426},
{time:1769987940,open:1.18428,high:1.18456,low:1.18417,close:1.1843},
{time:1769988000,open:1.18436,high:1.18436,low:1.18395,close:1.18412},
{time:1769988060,open:1.18415,high:1.18432,low:1.18403,close:1.18405},
{time:1769988120,open:1.18408,high:1.1841,low:1.18397,close:1.18405},
{time:1769988180,open:1.18405,high:1.1842,low:1.18401,close:1.18411},
{time:1769988240,open:1.18411,high:1.18411,low:1.18397,close:1.18398},
{time:1769988300,open:1.18399,high:1.18435,low:1.18399,close:1.18434},
{time:1769988360,open:1.18436,high:1.18466,low:1.18421,close:1.18466},
{time:1769988420,open:1.18462,high:1.18462,low:1.18426,close:1.18427},
{time:1769988480,open:1.18426,high:1.18431,low:1.18419,close:1.18429},
{time:1769988540,open:1.18431,high:1.1846,low:1.18431,close:1.18447},
{time:1769988600,open:1.18447,high:1.1847,low:1.18441,close:1.18461},
{time:1769988660,open:1.18462,high:1.18477,low:1.18442,close:1.18474},
{time:1769988720,open:1.18475,high:1.18492,low:1.1847,close:1.18471},
{time:1769988780,open:1.18472,high:1.18484,low:1.18464,close:1.18476},
{time:1769988840,open:1.18473,high:1.18496,low:1.18472,close:1.18493},
{time:1769988900,open:1.18494,high:1.18494,low:1.18472,close:1.18488},
{time:1769988960,open:1.18489,high:1.18495,low:1.18482,close:1.18484},
{time:1769989020,open:1.18484,high:1.18517,low:1.18484,close:1.18507},
{time:1769989080,open:1.18509,high:1.18531,low:1.18502,close:1.18529},
{time:1769989140,open:1.18529,high:1.18563,low:1.18524,close:1.18555},
{time:1769989200,open:1.18554,high:1.18561,low:1.18541,close:1.18558},
{time:1769989260,open:1.1856,high:1.18569,low:1.18512,close:1.18528},
{time:1769989320,open:1.18528,high:1.18535,low:1.18517,close:1.1853},
{time:1769989380,open:1.18531,high:1.18546,low:1.18524,close:1.18533},
{time:1769989440,open:1.18534,high:1.18556,low:1.18531,close:1.18553},
{time:1769989500,open:1.18552,high:1.18563,low:1.18521,close:1.18526},
{time:1769989560,open:1.18525,high:1.18543,low:1.18511,close:1.18533},
{time:1769989620,open:1.18533,high:1.18534,low:1.18501,close:1.18505},
{time:1769989680,open:1.18509,high:1.18509,low:1.18484,close:1.18489},
{time:1769989740,open:1.18492,high:1.18502,low:1.18486,close:1.18489},
{time:1769989800,open:1.18492,high:1.18494,low:1.18474,close:1.18489},
{time:1769989860,open:1.18491,high:1.18541,low:1.18491,close:1.18494},
{time:1769989920,open:1.18497,high:1.185,low:1.18485,close:1.18491},
{time:1769989980,open:1.18488,high:1.18515,low:1.18488,close:1.18504},
{time:1769990040,open:1.18508,high:1.18524,low:1.18502,close:1.18507},
{time:1769990100,open:1.18506,high:1.18506,low:1.18479,close:1.18483},
{time:1769990160,open:1.18484,high:1.18489,low:1.18474,close:1.18485},
{time:1769990220,open:1.18486,high:1.18495,low:1.18469,close:1.18473},
{time:1769990280,open:1.18473,high:1.18488,low:1.18468,close:1.18477},
{time:1769990340,open:1.18478,high:1.18478,low:1.18456,close:1.18461},
{time:1769990400,open:1.18464,high:1.18545,low:1.18463,close:1.1854},
{time:1769990460,open:1.18543,high:1.186,low:1.18543,close:1.18595},
{time:1769990520,open:1.18595,high:1.18608,low:1.18582,close:1.18596},
{time:1769990580,open:1.18597,high:1.18603,low:1.18579,close:1.18593},
{time:1769990640,open:1.18591,high:1.18624,low:1.1859,close:1.18619},
{time:1769990700,open:1.18619,high:1.18619,low:1.18588,close:1.18605},
{time:1769990760,open:1.18603,high:1.18622,low:1.18597,close:1.18612},
{time:1769990820,open:1.1861,high:1.1862,low:1.18587,close:1.18591},
{time:1769990880,open:1.18588,high:1.18696,low:1.18588,close:1.18688},
{time:1769990940,open:1.18687,high:1.18687,low:1.18634,close:1.18674},
{time:1769991000,open:1.18675,high:1.18697,low:1.18675,close:1.18686},
{time:1769991060,open:1.18684,high:1.18734,low:1.18684,close:1.18734},
{time:1769991120,open:1.18734,high:1.18736,low:1.18686,close:1.18704},
{time:1769991180,open:1.18702,high:1.18723,low:1.18699,close:1.18718},
{time:1769991240,open:1.1872,high:1.18728,low:1.18712,close:1.18721},
{time:1769991300,open:1.18721,high:1.18736,low:1.18716,close:1.18734},
{time:1769991360,open:1.18734,high:1.18742,low:1.18715,close:1.18715},
{time:1769991420,open:1.18719,high:1.18747,low:1.18716,close:1.18741},
{time:1769991480,open:1.18739,high:1.18742,low:1.18725,close:1.18727},
{time:1769991540,open:1.18726,high:1.1874,low:1.18715,close:1.18727},
{time:1769991600,open:1.1873,high:1.18732,low:1.18691,close:1.18692},
{time:1769991660,open:1.18692,high:1.18719,low:1.1869,close:1.18714},
{time:1769991720,open:1.18713,high:1.18716,low:1.18692,close:1.18702},
{time:1769991780,open:1.18702,high:1.18707,low:1.18685,close:1.18701},
{time:1769991840,open:1.18702,high:1.18716,low:1.18699,close:1.18705},
{time:1769991900,open:1.18704,high:1.18714,low:1.18696,close:1.18707},
{time:1769991960,open:1.18706,high:1.18726,low:1.18703,close:1.18719},
{time:1769992020,open:1.18717,high:1.18719,low:1.18698,close:1.187},
{time:1769992080,open:1.18699,high:1.18708,low:1.18697,close:1.18706},
{time:1769992140,open:1.18706,high:1.18728,low:1.18706,close:1.18716},
{time:1769992200,open:1.18716,high:1.18731,low:1.1871,close:1.18723},
{time:1769992260,open:1.18722,high:1.18731,low:1.18708,close:1.18717},
{time:1769992320,open:1.18715,high:1.18717,low:1.18696,close:1.18699},
{time:1769992380,open:1.18697,high:1.18717,low:1.18691,close:1.18698},
{time:1769992440,open:1.187,high:1.18701,low:1.18678,close:1.18684},
{time:1769992500,open:1.18685,high:1.18691,low:1.18674,close:1.18684},
{time:1769992560,open:1.18687,high:1.18702,low:1.18687,close:1.18687},
{time:1769992620,open:1.18688,high:1.18692,low:1.1868,close:1.18688},
{time:1769992680,open:1.18688,high:1.18703,low:1.18688,close:1.18688},
{time:1769992740,open:1.18688,high:1.18695,low:1.1868,close:1.1868},
{time:1769992800,open:1.1868,high:1.18688,low:1.18672,close:1.18679},
{time:1769992860,open:1.18678,high:1.18678,low:1.18666,close:1.18669},
{time:1769992920,open:1.18669,high:1.18671,low:1.18661,close:1.18666},
{time:1769992980,open:1.18665,high:1.18701,low:1.18665,close:1.18678},
{time:1769993040,open:1.18678,high:1.18685,low:1.18659,close:1.18666},
{time:1769993100,open:1.18664,high:1.18672,low:1.18658,close:1.18664},
{time:1769993160,open:1.18664,high:1.18677,low:1.18662,close:1.1867},
{time:1769993220,open:1.18673,high:1.18676,low:1.1864,close:1.18645},
{time:1769993280,open:1.18647,high:1.18662,low:1.18643,close:1.1866},
{time:1769993340,open:1.18659,high:1.18664,low:1.18648,close:1.18658},
{time:1769993400,open:1.1866,high:1.1866,low:1.18643,close:1.18645},
{time:1769993460,open:1.18645,high:1.18654,low:1.1864,close:1.18646},
{time:1769993520,open:1.18647,high:1.18668,low:1.18645,close:1.18653},
{time:1769993580,open:1.18653,high:1.18674,low:1.18648,close:1.18658},
{time:1769993640,open:1.18656,high:1.18669,low:1.18645,close:1.18646},
{time:1769993700,open:1.18645,high:1.18648,low:1.18621,close:1.18624},
{time:1769993760,open:1.18621,high:1.18637,low:1.18615,close:1.18626},
{time:1769993820,open:1.18621,high:1.18658,low:1.18612,close:1.18648},
{time:1769993880,open:1.18649,high:1.1866,low:1.18635,close:1.18657},
{time:1769993940,open:1.18656,high:1.18668,low:1.18647,close:1.18667},
{time:1769994000,open:1.18666,high:1.18688,low:1.18642,close:1.18662},
{time:1769994060,open:1.18665,high:1.1869,low:1.18649,close:1.1866},
{time:1769994120,open:1.18662,high:1.18674,low:1.18637,close:1.18663},
{time:1769994180,open:1.18666,high:1.18676,low:1.18655,close:1.18661},
{time:1769994240,open:1.18658,high:1.18662,low:1.18626,close:1.18635},
{time:1769994300,open:1.18635,high:1.18646,low:1.18608,close:1.18608},
{time:1769994360,open:1.18607,high:1.18618,low:1.18573,close:1.18584},
{time:1769994420,open:1.18584,high:1.18591,low:1.18579,close:1.18588},
{time:1769994480,open:1.18591,high:1.1863,low:1.1859,close:1.18624},
{time:1769994540,open:1.18624,high:1.18624,low:1.18571,close:1.18575},
{time:1769994600,open:1.18579,high:1.18581,low:1.18553,close:1.18562},
{time:1769994660,open:1.18564,high:1.18567,low:1.18546,close:1.18551},
{time:1769994720,open:1.18554,high:1.18573,low:1.18545,close:1.18546},
{time:1769994780,open:1.18548,high:1.18557,low:1.18536,close:1.18545},
{time:1769994840,open:1.18547,high:1.18553,low:1.18527,close:1.18543},
{time:1769994900,open:1.18544,high:1.18555,low:1.18528,close:1.18551},
{time:1769994960,open:1.18552,high:1.18613,low:1.1854,close:1.18609},
{time:1769995020,open:1.18609,high:1.18613,low:1.18591,close:1.18605},
{time:1769995080,open:1.18605,high:1.18614,low:1.18596,close:1.18608},
{time:1769995140,open:1.18606,high:1.1862,low:1.1859,close:1.18605},
{time:1769995200,open:1.18606,high:1.18617,low:1.18588,close:1.18597},
{time:1769995260,open:1.18595,high:1.18603,low:1.18564,close:1.18574},
{time:1769995320,open:1.18576,high:1.1859,low:1.18571,close:1.18581},
{time:1769995380,open:1.18578,high:1.18578,low:1.18552,close:1.18572},
{time:1769995440,open:1.18572,high:1.18614,low:1.18572,close:1.18606},
{time:1769995500,open:1.18605,high:1.18617,low:1.18602,close:1.18616},
{time:1769995560,open:1.18615,high:1.18633,low:1.18609,close:1.18629},
{time:1769995620,open:1.1863,high:1.18641,low:1.18611,close:1.18617},
{time:1769995680,open:1.18618,high:1.18627,low:1.18612,close:1.18621},
{time:1769995740,open:1.18624,high:1.18632,low:1.18617,close:1.18621},
{time:1769995800,open:1.18622,high:1.18635,low:1.18607,close:1.18607},
{time:1769995860,open:1.18609,high:1.18618,low:1.186,close:1.18609},
{time:1769995920,open:1.18611,high:1.18618,low:1.18596,close:1.18616},
{time:1769995980,open:1.18614,high:1.18647,low:1.18613,close:1.18644},
{time:1769996040,open:1.18644,high:1.18648,low:1.1863,close:1.18644},
{time:1769996100,open:1.18643,high:1.18686,low:1.18643,close:1.18682},
{time:1769996160,open:1.18683,high:1.18685,low:1.18667,close:1.18674},
{time:1769996220,open:1.18673,high:1.18673,low:1.18655,close:1.18659},
{time:1769996280,open:1.1866,high:1.18685,low:1.1866,close:1.18677},
{time:1769996340,open:1.18681,high:1.18688,low:1.18664,close:1.18667},
{time:1769996400,open:1.18666,high:1.18687,low:1.18658,close:1.1867},
{time:1769996460,open:1.18668,high:1.1867,low:1.18632,close:1.18646},
{time:1769996520,open:1.18646,high:1.18679,low:1.1864,close:1.18672},
{time:1769996580,open:1.18669,high:1.18669,low:1.18632,close:1.18656},
{time:1769996640,open:1.18657,high:1.18669,low:1.18644,close:1.18668},
{time:1769996700,open:1.1867,high:1.18693,low:1.1867,close:1.18692},
{time:1769996760,open:1.18693,high:1.18702,low:1.18678,close:1.18702},
{time:1769996820,open:1.18703,high:1.18714,low:1.18699,close:1.18711},
{time:1769996880,open:1.18713,high:1.18718,low:1.18701,close:1.18705},
{time:1769996940,open:1.18703,high:1.18713,low:1.18686,close:1.18686},
{time:1769997000,open:1.18687,high:1.1869,low:1.18666,close:1.18682},
{time:1769997060,open:1.18684,high:1.18689,low:1.18663,close:1.18682},
{time:1769997120,open:1.18681,high:1.18695,low:1.18668,close:1.1867},
{time:1769997180,open:1.18673,high:1.18688,low:1.18669,close:1.18688},
{time:1769997240,open:1.18688,high:1.18701,low:1.18664,close:1.18671},
{time:1769997300,open:1.18672,high:1.18696,low:1.18672,close:1.18688},
{time:1769997360,open:1.18687,high:1.18692,low:1.18679,close:1.18691},
{time:1769997420,open:1.1869,high:1.18717,low:1.18684,close:1.18715},
{time:1769997480,open:1.18715,high:1.18725,low:1.18705,close:1.18707},
{time:1769997540,open:1.18706,high:1.18723,low:1.18706,close:1.18706},
{time:1769997600,open:1.18707,high:1.18744,low:1.18705,close:1.18729},
{time:1769997660,open:1.18728,high:1.18746,low:1.18721,close:1.18727},
{time:1769997720,open:1.1873,high:1.18745,low:1.18717,close:1.1872},
{time:1769997780,open:1.18722,high:1.18732,low:1.18692,close:1.18692},
{time:1769997840,open:1.18692,high:1.18702,low:1.1869,close:1.18692},
{time:1769997900,open:1.18693,high:1.18693,low:1.18668,close:1.18673},
{time:1769997960,open:1.18672,high:1.18696,low:1.18667,close:1.18696},
{time:1769998020,open:1.18693,high:1.18709,low:1.18681,close:1.18709},
{time:1769998080,open:1.18706,high:1.18731,low:1.18705,close:1.18718},
{time:1769998140,open:1.18719,high:1.18727,low:1.18704,close:1.18711},
{time:1769998200,open:1.1871,high:1.18719,low:1.18687,close:1.18689},
{time:1769998260,open:1.1869,high:1.18719,low:1.18689,close:1.18701},
{time:1769998320,open:1.18704,high:1.18715,low:1.18682,close:1.18702},
{time:1769998380,open:1.18702,high:1.18702,low:1.18682,close:1.18688},
{time:1769998440,open:1.18686,high:1.18691,low:1.18673,close:1.18678},
{time:1769998500,open:1.1868,high:1.18688,low:1.18674,close:1.18686},
{time:1769998560,open:1.18684,high:1.18686,low:1.18673,close:1.18678},
{time:1769998620,open:1.18679,high:1.18691,low:1.18677,close:1.1868},
{time:1769998680,open:1.1868,high:1.187,low:1.18673,close:1.18699},
{time:1769998740,open:1.18698,high:1.18716,low:1.18694,close:1.1871},
{time:1769998800,open:1.18713,high:1.1873,low:1.18713,close:1.18723},
{time:1769998860,open:1.18723,high:1.18723,low:1.18691,close:1.18693},
{time:1769998920,open:1.18694,high:1.1871,low:1.18682,close:1.18705},
{time:1769998980,open:1.18702,high:1.18702,low:1.18679,close:1.18684},
{time:1769999040,open:1.18683,high:1.187,low:1.18679,close:1.18692},
{time:1769999100,open:1.18691,high:1.18696,low:1.18681,close:1.18693},
{time:1769999160,open:1.18694,high:1.18697,low:1.18679,close:1.1868},
{time:1769999220,open:1.1868,high:1.1869,low:1.18678,close:1.18684},
{time:1769999280,open:1.18685,high:1.187,low:1.18684,close:1.18688},
{time:1769999340,open:1.18689,high:1.18697,low:1.18668,close:1.18668},
{time:1769999400,open:1.18668,high:1.18698,low:1.18668,close:1.18697},
{time:1769999460,open:1.18695,high:1.18701,low:1.18691,close:1.187},
{time:1769999520,open:1.18702,high:1.18723,low:1.187,close:1.18712},
{time:1769999580,open:1.1871,high:1.18717,low:1.18691,close:1.18698},
{time:1769999640,open:1.18698,high:1.18712,low:1.18686,close:1.18687},
{time:1769999700,open:1.18686,high:1.18696,low:1.18674,close:1.18696},
{time:1769999760,open:1.18696,high:1.18706,low:1.18687,close:1.18704},
{time:1769999820,open:1.18706,high:1.18723,low:1.18706,close:1.1872},
{time:1769999880,open:1.18719,high:1.18724,low:1.18712,close:1.18717},
{time:1769999940,open:1.18718,high:1.18718,low:1.18701,close:1.18701},
{time:1770000000,open:1.18701,high:1.18709,low:1.18691,close:1.18691},
{time:1770000060,open:1.1869,high:1.18711,low:1.1869,close:1.18698},
{time:1770000120,open:1.18697,high:1.18724,low:1.18696,close:1.18723},
{time:1770000180,open:1.18726,high:1.18741,low:1.18722,close:1.1873},
{time:1770000240,open:1.18729,high:1.18735,low:1.18724,close:1.18735},
{time:1770000300,open:1.18733,high:1.18744,low:1.18727,close:1.18744},
{time:1770000360,open:1.18746,high:1.18747,low:1.18727,close:1.18742},
{time:1770000420,open:1.18744,high:1.18744,low:1.18698,close:1.18704},
{time:1770000480,open:1.18701,high:1.18701,low:1.1867,close:1.18686},
{time:1770000540,open:1.18686,high:1.18701,low:1.18673,close:1.18694},
{time:1770000600,open:1.18694,high:1.18696,low:1.18675,close:1.18677},
{time:1770000660,open:1.18677,high:1.18691,low:1.18668,close:1.18684},
{time:1770000720,open:1.18682,high:1.18688,low:1.18673,close:1.18673},
{time:1770000780,open:1.18674,high:1.187,low:1.18669,close:1.18692},
{time:1770000840,open:1.18692,high:1.18703,low:1.18692,close:1.18703},
{time:1770000900,open:1.18704,high:1.18708,low:1.18694,close:1.18694},
{time:1770000960,open:1.18695,high:1.18695,low:1.18677,close:1.18677},
{time:1770001020,open:1.18675,high:1.18676,low:1.18649,close:1.1865},
{time:1770001080,open:1.1865,high:1.18658,low:1.18639,close:1.18639},
{time:1770001140,open:1.18639,high:1.18639,low:1.18629,close:1.18631},
{time:1770001200,open:1.1863,high:1.18669,low:1.18629,close:1.18665},
{time:1770001260,open:1.18667,high:1.18672,low:1.18649,close:1.1865},
{time:1770001320,open:1.18651,high:1.18666,low:1.1865,close:1.18664},
{time:1770001380,open:1.18662,high:1.18675,low:1.18659,close:1.18666},
{time:1770001440,open:1.18664,high:1.18671,low:1.1866,close:1.18664},
{time:1770001500,open:1.18663,high:1.18681,low:1.18661,close:1.18672},
{time:1770001560,open:1.1867,high:1.18691,low:1.1867,close:1.18689},
{time:1770001620,open:1.18688,high:1.1869,low:1.18683,close:1.18687},
{time:1770001680,open:1.18686,high:1.18695,low:1.18677,close:1.18691},
{time:1770001740,open:1.18689,high:1.18689,low:1.18679,close:1.18686},
{time:1770001800,open:1.18686,high:1.18696,low:1.18679,close:1.18691},
{time:1770001860,open:1.18692,high:1.18701,low:1.18686,close:1.1869},
{time:1770001920,open:1.18692,high:1.18695,low:1.1868,close:1.1868},
{time:1770001980,open:1.18682,high:1.18685,low:1.18663,close:1.18671},
{time:1770002040,open:1.18672,high:1.18678,low:1.18654,close:1.18655},
{time:1770002100,open:1.18656,high:1.1866,low:1.1864,close:1.18642},
{time:1770002160,open:1.18644,high:1.18645,low:1.18631,close:1.18645},
{time:1770002220,open:1.18646,high:1.18674,low:1.18643,close:1.1867},
{time:1770002280,open:1.18672,high:1.18678,low:1.18668,close:1.18676},
{time:1770002340,open:1.18676,high:1.18695,low:1.18672,close:1.18693},
{time:1770002400,open:1.18693,high:1.18705,low:1.18692,close:1.18699},
{time:1770002460,open:1.18696,high:1.18705,low:1.18695,close:1.18705},
{time:1770002520,open:1.18706,high:1.18718,low:1.18697,close:1.18699},
{time:1770002580,open:1.18699,high:1.18706,low:1.18699,close:1.18704},
{time:1770002640,open:1.18703,high:1.18706,low:1.18685,close:1.18691},
{time:1770002700,open:1.18692,high:1.18694,low:1.18689,close:1.1869},
{time:1770002760,open:1.18689,high:1.18693,low:1.18686,close:1.18691},
{time:1770002820,open:1.18691,high:1.18694,low:1.18684,close:1.18687},
{time:1770002880,open:1.18686,high:1.18693,low:1.18674,close:1.18693},
{time:1770002940,open:1.18692,high:1.18692,low:1.18682,close:1.18682},
{time:1770003000,open:1.18683,high:1.18702,low:1.18676,close:1.18682},
{time:1770003060,open:1.18681,high:1.18696,low:1.18673,close:1.18679},
{time:1770003120,open:1.1868,high:1.1869,low:1.18677,close:1.18683},
{time:1770003180,open:1.18683,high:1.18684,low:1.18664,close:1.18669},
{time:1770003240,open:1.18671,high:1.18676,low:1.18663,close:1.18663},
{time:1770003300,open:1.18664,high:1.18675,low:1.18644,close:1.18644},
{time:1770003360,open:1.18646,high:1.18659,low:1.18646,close:1.18658},
{time:1770003420,open:1.18659,high:1.18683,low:1.18659,close:1.1868},
{time:1770003480,open:1.18682,high:1.18682,low:1.18665,close:1.18665},
{time:1770003540,open:1.18666,high:1.18666,low:1.18641,close:1.18653},
{time:1770003600,open:1.18651,high:1.18657,low:1.18635,close:1.18636},
{time:1770003660,open:1.18635,high:1.18649,low:1.18632,close:1.18646},
{time:1770003720,open:1.18646,high:1.18674,low:1.18644,close:1.18674},
{time:1770003780,open:1.18675,high:1.18699,low:1.18669,close:1.18695},
{time:1770003840,open:1.18696,high:1.18698,low:1.18681,close:1.18685},
{time:1770003900,open:1.18686,high:1.18689,low:1.18676,close:1.18683},
{time:1770003960,open:1.18685,high:1.18699,low:1.18683,close:1.18692},
{time:1770004020,open:1.18693,high:1.187,low:1.18693,close:1.18696},
{time:1770004080,open:1.18696,high:1.18705,low:1.18694,close:1.187},
{time:1770004140,open:1.18702,high:1.18713,low:1.18699,close:1.18709},
{time:1770004200,open:1.18708,high:1.18709,low:1.18694,close:1.18696},
{time:1770004260,open:1.18698,high:1.18709,low:1.18698,close:1.18705},
{time:1770004320,open:1.18702,high:1.18702,low:1.18696,close:1.18698},
{time:1770004380,open:1.18695,high:1.18704,low:1.18679,close:1.18679},
{time:1770004440,open:1.18681,high:1.18682,low:1.18671,close:1.18671},
{time:1770004500,open:1.18671,high:1.18683,low:1.1867,close:1.18683},
{time:1770004560,open:1.18684,high:1.18684,low:1.18669,close:1.1867},
{time:1770004620,open:1.18671,high:1.18673,low:1.18649,close:1.1865},
{time:1770004680,open:1.18647,high:1.18659,low:1.18642,close:1.18654},
{time:1770004740,open:1.18655,high:1.1866,low:1.18635,close:1.18641},
{time:1770004800,open:1.18641,high:1.18662,low:1.18641,close:1.18649},
{time:1770004860,open:1.18651,high:1.1866,low:1.18637,close:1.18646},
{time:1770004920,open:1.18645,high:1.18667,low:1.18629,close:1.18629},
{time:1770004980,open:1.18631,high:1.18665,low:1.18631,close:1.18662},
{time:1770005040,open:1.18663,high:1.18695,low:1.18652,close:1.18653},
{time:1770005100,open:1.18651,high:1.18669,low:1.18651,close:1.18663},
{time:1770005160,open:1.18665,high:1.1868,low:1.18661,close:1.18665},
{time:1770005220,open:1.18667,high:1.18678,low:1.18662,close:1.18672},
{time:1770005280,open:1.18672,high:1.18696,low:1.1867,close:1.18689},
{time:1770005340,open:1.18691,high:1.18708,low:1.18691,close:1.187},
{time:1770005400,open:1.18698,high:1.18725,low:1.18695,close:1.18695},
{time:1770005460,open:1.18695,high:1.18699,low:1.18682,close:1.18692},
{time:1770005520,open:1.18692,high:1.18693,low:1.18665,close:1.18684},
{time:1770005580,open:1.18684,high:1.18698,low:1.18683,close:1.18683},
{time:1770005640,open:1.18681,high:1.18692,low:1.18679,close:1.18681},
{time:1770005700,open:1.18682,high:1.18692,low:1.1868,close:1.18684},
{time:1770005760,open:1.18685,high:1.18689,low:1.18675,close:1.18679},
{time:1770005820,open:1.1868,high:1.1868,low:1.1867,close:1.18673},
{time:1770005880,open:1.18673,high:1.18676,low:1.1866,close:1.18667},
{time:1770005940,open:1.18668,high:1.18685,low:1.18668,close:1.18685},
{time:1770006000,open:1.18683,high:1.18683,low:1.18674,close:1.18677},
{time:1770006060,open:1.18676,high:1.18678,low:1.18667,close:1.18667},
{time:1770006120,open:1.1867,high:1.18674,low:1.18661,close:1.18664},
{time:1770006180,open:1.18667,high:1.18668,low:1.18659,close:1.18665},
{time:1770006240,open:1.18663,high:1.18667,low:1.18656,close:1.18657},
{time:1770006300,open:1.18658,high:1.18671,low:1.18651,close:1.18654},
{time:1770006360,open:1.18652,high:1.18655,low:1.18645,close:1.18645},
{time:1770006420,open:1.18647,high:1.1865,low:1.18638,close:1.1864},
{time:1770006480,open:1.1864,high:1.18649,low:1.18634,close:1.18645},
{time:1770006540,open:1.18642,high:1.18642,low:1.18627,close:1.18632},
{time:1770006600,open:1.1863,high:1.18639,low:1.18624,close:1.18631},
{time:1770006660,open:1.18633,high:1.18651,low:1.18628,close:1.18651},
{time:1770006720,open:1.18649,high:1.18651,low:1.18642,close:1.18649},
{time:1770006780,open:1.1865,high:1.18661,low:1.18649,close:1.1866},
{time:1770006840,open:1.18661,high:1.18664,low:1.18615,close:1.18631},
{time:1770006900,open:1.1863,high:1.18635,low:1.18622,close:1.18632},
{time:1770006960,open:1.1863,high:1.18644,low:1.18628,close:1.18634},
{time:1770007020,open:1.18634,high:1.18636,low:1.18608,close:1.1861},
{time:1770007080,open:1.1861,high:1.18614,low:1.18602,close:1.18609},
{time:1770007140,open:1.18608,high:1.18609,low:1.18596,close:1.18599},
{time:1770007200,open:1.18603,high:1.18606,low:1.18595,close:1.18597},
{time:1770007260,open:1.18596,high:1.18596,low:1.18584,close:1.18585},
{time:1770007320,open:1.18585,high:1.18586,low:1.1858,close:1.18581},
{time:1770007380,open:1.18582,high:1.18604,low:1.18582,close:1.186},
{time:1770007440,open:1.186,high:1.18603,low:1.18595,close:1.18601},
{time:1770007500,open:1.18601,high:1.1861,low:1.18601,close:1.1861},
{time:1770007560,open:1.18611,high:1.18624,low:1.18611,close:1.18624},
{time:1770007620,open:1.18624,high:1.18631,low:1.18618,close:1.18618},
{time:1770007680,open:1.18619,high:1.18619,low:1.18602,close:1.18606},
{time:1770007740,open:1.18605,high:1.1861,low:1.18605,close:1.18606},
{time:1770007800,open:1.18606,high:1.18611,low:1.18598,close:1.1861},
{time:1770007860,open:1.18611,high:1.18619,low:1.18605,close:1.18605},
{time:1770007920,open:1.18605,high:1.18605,low:1.186,close:1.18604},
{time:1770007980,open:1.18604,high:1.18607,low:1.18599,close:1.18605},
{time:1770008040,open:1.18607,high:1.18615,low:1.18607,close:1.18612},
{time:1770008100,open:1.18613,high:1.18614,low:1.18606,close:1.18611},
{time:1770008160,open:1.18611,high:1.18626,low:1.18601,close:1.18626},
{time:1770008220,open:1.18626,high:1.18632,low:1.18614,close:1.18617},
{time:1770008280,open:1.18619,high:1.18623,low:1.18615,close:1.18618},
{time:1770008340,open:1.18618,high:1.18636,low:1.18617,close:1.18631},
{time:1770008400,open:1.1863,high:1.1863,low:1.18621,close:1.18628},
{time:1770008460,open:1.18627,high:1.18629,low:1.18619,close:1.18626},
{time:1770008520,open:1.18629,high:1.18638,low:1.18623,close:1.18625},
{time:1770008580,open:1.18624,high:1.18631,low:1.18609,close:1.18629},
{time:1770008640,open:1.18627,high:1.18631,low:1.18619,close:1.18621},
{time:1770008700,open:1.1862,high:1.18621,low:1.1861,close:1.1861},
{time:1770008760,open:1.18613,high:1.18614,low:1.18583,close:1.18587},
{time:1770008820,open:1.18587,high:1.18613,low:1.18585,close:1.18602},
{time:1770008880,open:1.18605,high:1.18605,low:1.18596,close:1.18599},
{time:1770008940,open:1.18599,high:1.18613,low:1.18598,close:1.18613},
{time:1770009000,open:1.1861,high:1.18621,low:1.18609,close:1.1862},
{time:1770009060,open:1.18624,high:1.18629,low:1.18611,close:1.18613},
{time:1770009120,open:1.18611,high:1.18617,low:1.186,close:1.18608},
{time:1770009180,open:1.18606,high:1.18607,low:1.18604,close:1.18604},
{time:1770009240,open:1.18605,high:1.18613,low:1.18605,close:1.18612},
{time:1770009300,open:1.18613,high:1.18619,low:1.18606,close:1.18609},
{time:1770009360,open:1.1861,high:1.18623,low:1.18609,close:1.18621},
{time:1770009420,open:1.18622,high:1.18625,low:1.18619,close:1.18621},
{time:1770009480,open:1.18623,high:1.18625,low:1.18623,close:1.18624},
{time:1770009540,open:1.18621,high:1.18621,low:1.18614,close:1.18616},
{time:1770009600,open:1.18617,high:1.18629,low:1.18616,close:1.18629},
{time:1770009660,open:1.18631,high:1.18631,low:1.18608,close:1.1861},
{time:1770009720,open:1.18609,high:1.1861,low:1.18602,close:1.18605},
{time:1770009780,open:1.18604,high:1.1861,low:1.18604,close:1.1861},
{time:1770009840,open:1.1861,high:1.18616,low:1.18604,close:1.18606},
{time:1770009900,open:1.18605,high:1.18605,low:1.18585,close:1.18588},
{time:1770009960,open:1.18588,high:1.18588,low:1.18562,close:1.18562},
{time:1770010020,open:1.18562,high:1.18564,low:1.18556,close:1.18558},
{time:1770010080,open:1.18559,high:1.18559,low:1.18528,close:1.18543},
{time:1770010140,open:1.18545,high:1.18547,low:1.1852,close:1.18546},
{time:1770010200,open:1.18549,high:1.18549,low:1.18527,close:1.18534},
{time:1770010260,open:1.18537,high:1.18549,low:1.18533,close:1.18548},
{time:1770010320,open:1.18549,high:1.18564,low:1.18546,close:1.18559},
{time:1770010380,open:1.18559,high:1.1856,low:1.18549,close:1.18551},
{time:1770010440,open:1.18555,high:1.18555,low:1.18541,close:1.18551},
{time:1770010500,open:1.18552,high:1.18552,low:1.18542,close:1.18545},
{time:1770010560,open:1.18546,high:1.18549,low:1.18534,close:1.18537},
{time:1770010620,open:1.18536,high:1.18552,low:1.18534,close:1.1854},
{time:1770010680,open:1.1854,high:1.18553,low:1.18534,close:1.18547},
{time:1770010740,open:1.18549,high:1.18549,low:1.18534,close:1.18539},
{time:1770010800,open:1.18538,high:1.18541,low:1.18524,close:1.18541},
{time:1770010860,open:1.18542,high:1.18544,low:1.18527,close:1.18527},
{time:1770010920,open:1.18529,high:1.18532,low:1.18513,close:1.18515},
{time:1770010980,open:1.18514,high:1.18539,low:1.18511,close:1.18524},
{time:1770011040,open:1.18524,high:1.18529,low:1.18518,close:1.18524},
{time:1770011100,open:1.18522,high:1.1853,low:1.18514,close:1.18517},
{time:1770011160,open:1.18517,high:1.18524,low:1.18509,close:1.18522},
{time:1770011220,open:1.18523,high:1.18523,low:1.18475,close:1.18475},
{time:1770011280,open:1.18476,high:1.18483,low:1.18456,close:1.18459},
{time:1770011340,open:1.18459,high:1.18472,low:1.1845,close:1.18464},
{time:1770011400,open:1.18463,high:1.1847,low:1.18452,close:1.18461},
{time:1770011460,open:1.18463,high:1.18473,low:1.18445,close:1.18453},
{time:1770011520,open:1.18452,high:1.18455,low:1.18415,close:1.18427},
{time:1770011580,open:1.18428,high:1.18436,low:1.18409,close:1.18436},
{time:1770011640,open:1.18434,high:1.18464,low:1.18433,close:1.18463},
{time:1770011700,open:1.18465,high:1.18483,low:1.18465,close:1.18476},
{time:1770011760,open:1.18474,high:1.18484,low:1.18458,close:1.18458},
{time:1770011820,open:1.1846,high:1.18484,low:1.1846,close:1.18479},
{time:1770011880,open:1.18477,high:1.18504,low:1.18477,close:1.18504},
{time:1770011940,open:1.18503,high:1.18509,low:1.18494,close:1.18506},
{time:1770012000,open:1.18507,high:1.18526,low:1.18507,close:1.18526},
{time:1770012060,open:1.18525,high:1.18527,low:1.18495,close:1.18497},
{time:1770012120,open:1.18495,high:1.18509,low:1.18485,close:1.18487},
{time:1770012180,open:1.18486,high:1.18486,low:1.18469,close:1.1848},
{time:1770012240,open:1.18479,high:1.18495,low:1.18474,close:1.18475},
{time:1770012300,open:1.18478,high:1.18491,low:1.18471,close:1.18477},
{time:1770012360,open:1.18476,high:1.1849,low:1.18446,close:1.18463},
{time:1770012420,open:1.18464,high:1.1847,low:1.18449,close:1.18458},
{time:1770012480,open:1.18459,high:1.18495,low:1.18459,close:1.18492},
{time:1770012540,open:1.18494,high:1.18515,low:1.18491,close:1.18515},
{time:1770012600,open:1.18516,high:1.18526,low:1.18512,close:1.18525},
{time:1770012660,open:1.18527,high:1.18544,low:1.18527,close:1.1853},
{time:1770012720,open:1.18531,high:1.18533,low:1.18499,close:1.185},
{time:1770012780,open:1.18496,high:1.18496,low:1.18464,close:1.18468},
{time:1770012840,open:1.18469,high:1.18481,low:1.18463,close:1.18476},
{time:1770012900,open:1.18479,high:1.18506,low:1.18471,close:1.18489},
{time:1770012960,open:1.1849,high:1.1849,low:1.18471,close:1.18471},
{time:1770013020,open:1.18473,high:1.18481,low:1.18464,close:1.18475},
{time:1770013080,open:1.18473,high:1.18494,low:1.18469,close:1.18479},
{time:1770013140,open:1.1848,high:1.18509,low:1.18478,close:1.18504},
{time:1770013200,open:1.18504,high:1.18505,low:1.18479,close:1.18484},
{time:1770013260,open:1.18485,high:1.18485,low:1.18454,close:1.18466},
{time:1770013320,open:1.18464,high:1.18472,low:1.18456,close:1.18456},
{time:1770013380,open:1.18455,high:1.18465,low:1.18435,close:1.18465},
{time:1770013440,open:1.18467,high:1.1848,low:1.1845,close:1.1845},
{time:1770013500,open:1.1845,high:1.18476,low:1.18449,close:1.18474},
{time:1770013560,open:1.18475,high:1.1848,low:1.1845,close:1.18451},
{time:1770013620,open:1.18449,high:1.18455,low:1.18432,close:1.18448},
{time:1770013680,open:1.18452,high:1.18471,low:1.18449,close:1.18451},
{time:1770013740,open:1.18453,high:1.18466,low:1.18452,close:1.18461},
{time:1770013800,open:1.18461,high:1.18503,low:1.18461,close:1.18488},
{time:1770013860,open:1.18491,high:1.18491,low:1.18464,close:1.18467},
{time:1770013920,open:1.18469,high:1.18495,low:1.18467,close:1.1849},
{time:1770013980,open:1.18491,high:1.18492,low:1.18474,close:1.18479},
{time:1770014040,open:1.1848,high:1.18509,low:1.1848,close:1.18509},
{time:1770014100,open:1.18507,high:1.18523,low:1.185,close:1.18505},
{time:1770014160,open:1.18505,high:1.18512,low:1.18493,close:1.18504},
{time:1770014220,open:1.18505,high:1.18506,low:1.18471,close:1.18476},
{time:1770014280,open:1.18473,high:1.18505,low:1.1847,close:1.18475},
{time:1770014340,open:1.18475,high:1.18482,low:1.18469,close:1.18475},
{time:1770014400,open:1.18476,high:1.18483,low:1.18455,close:1.18479},
{time:1770014460,open:1.18479,high:1.18482,low:1.18467,close:1.18477},
{time:1770014520,open:1.18476,high:1.1849,low:1.18476,close:1.18477},
{time:1770014580,open:1.18478,high:1.18499,low:1.18471,close:1.18495},
{time:1770014640,open:1.18497,high:1.18529,low:1.18487,close:1.18526},
{time:1770014700,open:1.18527,high:1.18528,low:1.18502,close:1.18511},
{time:1770014760,open:1.18513,high:1.1853,low:1.18511,close:1.18525},
{time:1770014820,open:1.18525,high:1.18529,low:1.185,close:1.18507},
{time:1770014880,open:1.1851,high:1.18531,low:1.18507,close:1.18519},
{time:1770014940,open:1.1852,high:1.18534,low:1.18516,close:1.18517},
{time:1770015000,open:1.18516,high:1.18536,low:1.18511,close:1.18511},
{time:1770015060,open:1.18508,high:1.18519,low:1.18503,close:1.18513},
{time:1770015120,open:1.18509,high:1.18524,low:1.18495,close:1.18509},
{time:1770015180,open:1.18509,high:1.18525,low:1.18505,close:1.18515},
{time:1770015240,open:1.18515,high:1.18537,low:1.1851,close:1.1853},
{time:1770015300,open:1.18533,high:1.18537,low:1.18511,close:1.18515},
{time:1770015360,open:1.18514,high:1.18537,low:1.18509,close:1.18524},
{time:1770015420,open:1.18523,high:1.18531,low:1.18512,close:1.18513},
{time:1770015480,open:1.18514,high:1.18523,low:1.18505,close:1.18522},
{time:1770015540,open:1.18523,high:1.18537,low:1.18523,close:1.18537},
{time:1770015600,open:1.18536,high:1.18538,low:1.18519,close:1.18529},
{time:1770015660,open:1.18527,high:1.18559,low:1.18519,close:1.18559},
{time:1770015720,open:1.1856,high:1.18566,low:1.18541,close:1.18552},
{time:1770015780,open:1.18554,high:1.18587,low:1.18552,close:1.18585},
{time:1770015840,open:1.18583,high:1.18596,low:1.18579,close:1.18582},
{time:1770015900,open:1.18584,high:1.18586,low:1.1857,close:1.18584},
{time:1770015960,open:1.18586,high:1.18607,low:1.18584,close:1.18602},
{time:1770016020,open:1.18601,high:1.18624,low:1.18592,close:1.18617},
{time:1770016080,open:1.18617,high:1.18619,low:1.18594,close:1.18607},
{time:1770016140,open:1.18606,high:1.18606,low:1.18568,close:1.18606},
{time:1770016200,open:1.18606,high:1.18618,low:1.18595,close:1.18605},
{time:1770016260,open:1.18607,high:1.18628,low:1.18598,close:1.18626},
{time:1770016320,open:1.18628,high:1.18628,low:1.18587,close:1.18588},
{time:1770016380,open:1.18587,high:1.18589,low:1.18571,close:1.18588},
{time:1770016440,open:1.18591,high:1.18591,low:1.1857,close:1.1857},
{time:1770016500,open:1.18568,high:1.18576,low:1.18546,close:1.18567},
{time:1770016560,open:1.18568,high:1.1857,low:1.18545,close:1.18547},
{time:1770016620,open:1.18548,high:1.18566,low:1.18541,close:1.18566},
{time:1770016680,open:1.18566,high:1.1858,low:1.18562,close:1.18576},
{time:1770016740,open:1.18575,high:1.18587,low:1.18571,close:1.18581},
{time:1770016800,open:1.1858,high:1.18615,low:1.18577,close:1.18605},
{time:1770016860,open:1.18605,high:1.18623,low:1.18603,close:1.1862},
{time:1770016920,open:1.18619,high:1.18619,low:1.18604,close:1.18609},
{time:1770016980,open:1.18608,high:1.18614,low:1.18595,close:1.18595},
{time:1770017040,open:1.18595,high:1.18604,low:1.18567,close:1.18571},
{time:1770017100,open:1.18573,high:1.18611,low:1.18573,close:1.18609},
{time:1770017160,open:1.18612,high:1.18627,low:1.18605,close:1.18625},
{time:1770017220,open:1.18621,high:1.18626,low:1.18604,close:1.18625},
{time:1770017280,open:1.18625,high:1.18628,low:1.1862,close:1.18627},
{time:1770017340,open:1.18626,high:1.18633,low:1.1862,close:1.18628},
{time:1770017400,open:1.18631,high:1.18631,low:1.18611,close:1.18615},
{time:1770017460,open:1.18614,high:1.18614,low:1.18562,close:1.18569},
{time:1770017520,open:1.18569,high:1.1857,low:1.18548,close:1.1856},
{time:1770017580,open:1.18559,high:1.18568,low:1.18551,close:1.18551},
{time:1770017640,open:1.18551,high:1.18575,low:1.18544,close:1.18546},
{time:1770017700,open:1.18545,high:1.18556,low:1.18539,close:1.1854},
{time:1770017760,open:1.18545,high:1.18555,low:1.18519,close:1.18542},
{time:1770017820,open:1.1854,high:1.18555,low:1.18528,close:1.18533},
{time:1770017880,open:1.18532,high:1.1855,low:1.18516,close:1.18526},
{time:1770017940,open:1.18526,high:1.18555,low:1.18524,close:1.18553},
{time:1770018000,open:1.18553,high:1.18564,low:1.18541,close:1.1856},
{time:1770018060,open:1.18561,high:1.18579,low:1.1856,close:1.18575},
{time:1770018120,open:1.18576,high:1.18582,low:1.18565,close:1.18575},
{time:1770018180,open:1.18575,high:1.18582,low:1.18561,close:1.18582},
{time:1770018240,open:1.18578,high:1.18578,low:1.18558,close:1.18567},
{time:1770018300,open:1.18568,high:1.18568,low:1.18545,close:1.18568},
{time:1770018360,open:1.18571,high:1.18571,low:1.18556,close:1.18561},
{time:1770018420,open:1.18557,high:1.18563,low:1.18545,close:1.18556},
{time:1770018480,open:1.18557,high:1.18595,low:1.18557,close:1.18594},
{time:1770018540,open:1.18593,high:1.18595,low:1.18564,close:1.18586},
{time:1770018600,open:1.18584,high:1.18587,low:1.18555,close:1.18558},
{time:1770018660,open:1.18558,high:1.18558,low:1.18522,close:1.18546},
{time:1770018720,open:1.18548,high:1.18549,low:1.18535,close:1.18537},
{time:1770018780,open:1.18536,high:1.18547,low:1.18519,close:1.18547},
{time:1770018840,open:1.18545,high:1.18576,low:1.1854,close:1.18571},
{time:1770018900,open:1.18575,high:1.18604,low:1.18574,close:1.18591},
{time:1770018960,open:1.18591,high:1.18594,low:1.1858,close:1.18581},
{time:1770019020,open:1.18578,high:1.18582,low:1.18542,close:1.18565},
{time:1770019080,open:1.18564,high:1.18564,low:1.18546,close:1.18551},
{time:1770019140,open:1.18549,high:1.18556,low:1.18531,close:1.18532},
{time:1770019200,open:1.18533,high:1.18595,low:1.18533,close:1.18576},
{time:1770019260,open:1.18576,high:1.18604,low:1.18568,close:1.18568},
{time:1770019320,open:1.18569,high:1.1857,low:1.18529,close:1.18569},
{time:1770019380,open:1.18568,high:1.18575,low:1.18559,close:1.18565},
{time:1770019440,open:1.18562,high:1.18582,low:1.18544,close:1.18574},
{time:1770019500,open:1.18573,high:1.18573,low:1.18521,close:1.18521},
{time:1770019560,open:1.18521,high:1.18521,low:1.1848,close:1.18509},
{time:1770019620,open:1.18511,high:1.18546,low:1.18507,close:1.18541},
{time:1770019680,open:1.18541,high:1.18563,low:1.18539,close:1.1856},
{time:1770019740,open:1.1856,high:1.18586,low:1.18549,close:1.18582},
{time:1770019800,open:1.18579,high:1.18591,low:1.18553,close:1.18585},
{time:1770019860,open:1.18584,high:1.18593,low:1.18564,close:1.18566},
{time:1770019920,open:1.18568,high:1.18604,low:1.18561,close:1.18596},
{time:1770019980,open:1.18596,high:1.18601,low:1.18577,close:1.18587},
{time:1770020040,open:1.18588,high:1.18617,low:1.18581,close:1.18617},
{time:1770020100,open:1.18617,high:1.18621,low:1.18578,close:1.18615},
{time:1770020160,open:1.18616,high:1.18638,low:1.18616,close:1.18623},
{time:1770020220,open:1.18625,high:1.1865,low:1.18621,close:1.18644},
{time:1770020280,open:1.18642,high:1.18659,low:1.18639,close:1.18652},
{time:1770020340,open:1.18651,high:1.18665,low:1.18639,close:1.1866},
{time:1770020400,open:1.1866,high:1.18661,low:1.18639,close:1.18644},
{time:1770020460,open:1.18643,high:1.18645,low:1.18623,close:1.1863},
{time:1770020520,open:1.18629,high:1.18629,low:1.1861,close:1.18616},
{time:1770020580,open:1.18616,high:1.18632,low:1.18616,close:1.18625},
{time:1770020640,open:1.18621,high:1.18628,low:1.18609,close:1.18609},
{time:1770020700,open:1.18609,high:1.18624,low:1.18594,close:1.18609},
{time:1770020760,open:1.18608,high:1.18613,low:1.18578,close:1.18591},
{time:1770020820,open:1.1859,high:1.18598,low:1.18573,close:1.18576},
{time:1770020880,open:1.18575,high:1.18607,low:1.18571,close:1.18602},
{time:1770020940,open:1.18603,high:1.18626,low:1.186,close:1.18624},
{time:1770021000,open:1.18621,high:1.18629,low:1.18615,close:1.18615},
{time:1770021060,open:1.18615,high:1.18624,low:1.18586,close:1.18612},
{time:1770021120,open:1.18608,high:1.18626,low:1.18595,close:1.18626},
{time:1770021180,open:1.18624,high:1.18631,low:1.18615,close:1.18617},
{time:1770021240,open:1.18615,high:1.18635,low:1.18607,close:1.18625},
{time:1770021300,open:1.18627,high:1.18642,low:1.18619,close:1.1864},
{time:1770021360,open:1.18641,high:1.18659,low:1.18629,close:1.18654},
{time:1770021420,open:1.18657,high:1.18657,low:1.18627,close:1.18627},
{time:1770021480,open:1.18626,high:1.18626,low:1.186,close:1.18608},
{time:1770021540,open:1.18606,high:1.18622,low:1.18593,close:1.18596},
{time:1770021600,open:1.18596,high:1.18601,low:1.1857,close:1.18574},
{time:1770021660,open:1.18574,high:1.18575,low:1.18554,close:1.18561},
{time:1770021720,open:1.1856,high:1.1857,low:1.18549,close:1.1857},
{time:1770021780,open:1.1857,high:1.18583,low:1.18568,close:1.18578},
{time:1770021840,open:1.18578,high:1.18583,low:1.18561,close:1.18565},
{time:1770021900,open:1.18561,high:1.18566,low:1.18552,close:1.18563},
{time:1770021960,open:1.18563,high:1.18575,low:1.18553,close:1.18565},
{time:1770022020,open:1.18569,high:1.18585,low:1.18569,close:1.18576},
{time:1770022080,open:1.18573,high:1.1858,low:1.18564,close:1.18575},
{time:1770022140,open:1.18578,high:1.18584,low:1.18564,close:1.18583},
{time:1770022200,open:1.18581,high:1.18601,low:1.18581,close:1.186},
{time:1770022260,open:1.18599,high:1.18599,low:1.18535,close:1.18538},
{time:1770022320,open:1.18538,high:1.1854,low:1.18508,close:1.18525},
{time:1770022380,open:1.18524,high:1.18544,low:1.1852,close:1.1854},
{time:1770022440,open:1.18539,high:1.18549,low:1.18517,close:1.18517},
{time:1770022500,open:1.18516,high:1.18525,low:1.18507,close:1.18515},
{time:1770022560,open:1.18515,high:1.18521,low:1.1849,close:1.1849},
{time:1770022620,open:1.1849,high:1.18492,low:1.18475,close:1.18478},
{time:1770022680,open:1.18477,high:1.18484,low:1.18439,close:1.1844},
{time:1770022740,open:1.18439,high:1.18511,low:1.18435,close:1.18507},
{time:1770022800,open:1.18507,high:1.1853,low:1.18493,close:1.18522},
{time:1770022860,open:1.18521,high:1.18531,low:1.18492,close:1.18506},
{time:1770022920,open:1.18504,high:1.18512,low:1.18472,close:1.18476},
{time:1770022980,open:1.18473,high:1.18478,low:1.18446,close:1.18471},
{time:1770023040,open:1.18474,high:1.18506,low:1.18473,close:1.18506},
{time:1770023100,open:1.18502,high:1.18521,low:1.18493,close:1.18512},
{time:1770023160,open:1.18515,high:1.18519,low:1.18494,close:1.18501},
{time:1770023220,open:1.18501,high:1.18536,low:1.18492,close:1.18534},
{time:1770023280,open:1.18533,high:1.18534,low:1.18524,close:1.18532},
{time:1770023340,open:1.1853,high:1.1853,low:1.18508,close:1.18524},
{time:1770023400,open:1.18525,high:1.18532,low:1.18515,close:1.18516},
{time:1770023460,open:1.18515,high:1.1853,low:1.18506,close:1.18526},
{time:1770023520,open:1.18526,high:1.1854,low:1.1852,close:1.18536},
{time:1770023580,open:1.1854,high:1.1854,low:1.18525,close:1.18525},
{time:1770023640,open:1.18528,high:1.18528,low:1.18512,close:1.1852},
{time:1770023700,open:1.18518,high:1.18518,low:1.18496,close:1.18501},
{time:1770023760,open:1.18499,high:1.18506,low:1.18483,close:1.18491},
{time:1770023820,open:1.18492,high:1.18516,low:1.18482,close:1.18489},
{time:1770023880,open:1.18487,high:1.18508,low:1.18481,close:1.185},
{time:1770023940,open:1.18498,high:1.18529,low:1.1849,close:1.18525},
{time:1770024000,open:1.18526,high:1.18526,low:1.18488,close:1.18488},
{time:1770024060,open:1.18489,high:1.18506,low:1.18477,close:1.18506},
{time:1770024120,open:1.18506,high:1.18538,low:1.18506,close:1.18514},
{time:1770024180,open:1.18513,high:1.18534,low:1.18511,close:1.18532},
{time:1770024240,open:1.18533,high:1.18544,low:1.18532,close:1.18544},
{time:1770024300,open:1.18543,high:1.18566,low:1.18543,close:1.18566},
{time:1770024360,open:1.18566,high:1.18593,low:1.18566,close:1.18593},
{time:1770024420,open:1.18591,high:1.18599,low:1.18572,close:1.18573},
{time:1770024480,open:1.18575,high:1.1858,low:1.1855,close:1.18559},
{time:1770024540,open:1.1856,high:1.18568,low:1.18544,close:1.18568},
{time:1770024600,open:1.18568,high:1.18586,low:1.18568,close:1.18585},
{time:1770024660,open:1.18585,high:1.18586,low:1.18573,close:1.18582},
{time:1770024720,open:1.18582,high:1.18582,low:1.18556,close:1.18559},
{time:1770024780,open:1.18559,high:1.1856,low:1.18545,close:1.18545},
{time:1770024840,open:1.18547,high:1.18551,low:1.18528,close:1.18542},
{time:1770024900,open:1.18545,high:1.18548,low:1.18525,close:1.18546},
{time:1770024960,open:1.18545,high:1.18559,low:1.18538,close:1.18548},
{time:1770025020,open:1.18546,high:1.18557,low:1.18522,close:1.18524},
{time:1770025080,open:1.18521,high:1.18545,low:1.18519,close:1.18519},
{time:1770025140,open:1.1852,high:1.18534,low:1.18519,close:1.18525},
{time:1770025200,open:1.18523,high:1.18546,low:1.18523,close:1.18545},
{time:1770025260,open:1.18547,high:1.18554,low:1.18536,close:1.18553},
{time:1770025320,open:1.18552,high:1.18575,low:1.18543,close:1.18574},
{time:1770025380,open:1.18571,high:1.18573,low:1.18549,close:1.18551},
{time:1770025440,open:1.18551,high:1.18554,low:1.18545,close:1.18553},
{time:1770025500,open:1.18552,high:1.18554,low:1.18539,close:1.18554},
{time:1770025560,open:1.18553,high:1.18566,low:1.18553,close:1.18555},
{time:1770025620,open:1.18556,high:1.18598,low:1.18556,close:1.18595},
{time:1770025680,open:1.18597,high:1.18602,low:1.18581,close:1.18594},
{time:1770025740,open:1.18594,high:1.18621,low:1.18583,close:1.18618},
{time:1770025800,open:1.1862,high:1.18635,low:1.18614,close:1.18632},
{time:1770025860,open:1.18631,high:1.18641,low:1.18626,close:1.18638},
{time:1770025920,open:1.18638,high:1.1865,low:1.18626,close:1.18646},
{time:1770025980,open:1.18647,high:1.18648,low:1.1862,close:1.18641},
{time:1770026040,open:1.18639,high:1.18647,low:1.18631,close:1.18647},
{time:1770026100,open:1.18644,high:1.18647,low:1.18631,close:1.18646},
{time:1770026160,open:1.18645,high:1.18648,low:1.18624,close:1.18645},
{time:1770026220,open:1.18645,high:1.18649,low:1.18635,close:1.1864},
{time:1770026280,open:1.18639,high:1.18646,low:1.18625,close:1.1863},
{time:1770026340,open:1.18629,high:1.18632,low:1.18616,close:1.18623},
{time:1770026400,open:1.18621,high:1.18641,low:1.18586,close:1.18626},
{time:1770026460,open:1.18626,high:1.18638,low:1.18621,close:1.18636},
{time:1770026520,open:1.18634,high:1.18673,low:1.18634,close:1.18651},
{time:1770026580,open:1.18652,high:1.18652,low:1.18627,close:1.18638},
{time:1770026640,open:1.18638,high:1.1864,low:1.18617,close:1.18621},
{time:1770026700,open:1.18623,high:1.18629,low:1.1862,close:1.18622},
{time:1770026760,open:1.18621,high:1.18629,low:1.18607,close:1.18617},
{time:1770026820,open:1.18617,high:1.18659,low:1.18617,close:1.1865},
{time:1770026880,open:1.1865,high:1.18652,low:1.18637,close:1.18651},
{time:1770026940,open:1.1865,high:1.18665,low:1.18645,close:1.18647},
{time:1770027000,open:1.18645,high:1.18667,low:1.18636,close:1.18661},
{time:1770027060,open:1.18661,high:1.18672,low:1.18659,close:1.18672},
{time:1770027120,open:1.18668,high:1.18685,low:1.18668,close:1.18682},
{time:1770027180,open:1.18681,high:1.1869,low:1.18678,close:1.18687},
{time:1770027240,open:1.18686,high:1.18687,low:1.18667,close:1.18685},
{time:1770027300,open:1.18686,high:1.18695,low:1.18684,close:1.18686},
{time:1770027360,open:1.18685,high:1.18688,low:1.18675,close:1.18685},
{time:1770027420,open:1.18687,high:1.18691,low:1.18676,close:1.18677},
{time:1770027480,open:1.18676,high:1.18677,low:1.1866,close:1.18675},
{time:1770027540,open:1.18672,high:1.18681,low:1.18667,close:1.1868},
{time:1770027600,open:1.18679,high:1.18687,low:1.1867,close:1.18685},
{time:1770027660,open:1.18685,high:1.18701,low:1.18653,close:1.18659},
{time:1770027720,open:1.18658,high:1.18675,low:1.18647,close:1.18651},
{time:1770027780,open:1.18653,high:1.18656,low:1.18642,close:1.1865},
{time:1770027840,open:1.18651,high:1.18665,low:1.18646,close:1.18646},
{time:1770027900,open:1.18646,high:1.18667,low:1.18645,close:1.18662},
{time:1770027960,open:1.18663,high:1.18668,low:1.18653,close:1.18659},
{time:1770028020,open:1.18659,high:1.18673,low:1.1865,close:1.1867},
{time:1770028080,open:1.18671,high:1.18676,low:1.18657,close:1.18666},
{time:1770028140,open:1.18666,high:1.18674,low:1.18649,close:1.18665},
{time:1770028200,open:1.18664,high:1.18669,low:1.18651,close:1.18654},
{time:1770028260,open:1.18655,high:1.18655,low:1.18634,close:1.18635},
{time:1770028320,open:1.18634,high:1.18651,low:1.18634,close:1.18649},
{time:1770028380,open:1.18646,high:1.18649,low:1.18634,close:1.18647},
{time:1770028440,open:1.18646,high:1.18664,low:1.18644,close:1.1865},
{time:1770028500,open:1.18651,high:1.18651,low:1.1861,close:1.18614},
{time:1770028560,open:1.18612,high:1.18636,low:1.18605,close:1.18614},
{time:1770028620,open:1.18615,high:1.1862,low:1.18601,close:1.18619},
{time:1770028680,open:1.18621,high:1.18636,low:1.18615,close:1.18633},
{time:1770028740,open:1.18631,high:1.18636,low:1.18619,close:1.18628},
{time:1770028800,open:1.18627,high:1.18644,low:1.18622,close:1.18631},
{time:1770028860,open:1.18631,high:1.18631,low:1.18609,close:1.1863},
{time:1770028920,open:1.18627,high:1.18627,low:1.18602,close:1.18606},
{time:1770028980,open:1.18607,high:1.18607,low:1.1859,close:1.18606},
{time:1770029040,open:1.18607,high:1.18609,low:1.18592,close:1.18599},
{time:1770029100,open:1.18598,high:1.18607,low:1.18593,close:1.18599},
{time:1770029160,open:1.18599,high:1.1861,low:1.18587,close:1.18605},
{time:1770029220,open:1.18606,high:1.18607,low:1.186,close:1.18602},
{time:1770029280,open:1.186,high:1.18616,low:1.18599,close:1.18612},
{time:1770029340,open:1.18612,high:1.18634,low:1.1861,close:1.18626},
{time:1770029400,open:1.18626,high:1.18628,low:1.18614,close:1.1862},
{time:1770029460,open:1.18618,high:1.18631,low:1.18614,close:1.1863},
{time:1770029520,open:1.18631,high:1.18638,low:1.18627,close:1.18634},
{time:1770029580,open:1.18632,high:1.18647,low:1.18625,close:1.1864},
{time:1770029640,open:1.18639,high:1.18647,low:1.18636,close:1.18636},
{time:1770029700,open:1.18635,high:1.18651,low:1.18635,close:1.18651},
{time:1770029760,open:1.1865,high:1.18672,low:1.1865,close:1.18664},
{time:1770029820,open:1.18664,high:1.1868,low:1.18645,close:1.1868},
{time:1770029880,open:1.18679,high:1.18679,low:1.1865,close:1.18652},
{time:1770029940,open:1.18651,high:1.18657,low:1.18646,close:1.18646},
{time:1770030000,open:1.18647,high:1.18681,low:1.18634,close:1.18676},
{time:1770030060,open:1.18678,high:1.18685,low:1.18664,close:1.18684},
{time:1770030120,open:1.18682,high:1.18691,low:1.18665,close:1.18679},
{time:1770030180,open:1.18679,high:1.1868,low:1.18648,close:1.1865},
{time:1770030240,open:1.1865,high:1.18651,low:1.18631,close:1.18646},
{time:1770030300,open:1.18645,high:1.18655,low:1.18636,close:1.18654},
{time:1770030360,open:1.18654,high:1.18665,low:1.18651,close:1.18665},
{time:1770030420,open:1.18663,high:1.18676,low:1.1866,close:1.18666},
{time:1770030480,open:1.18667,high:1.18667,low:1.18644,close:1.18644},
{time:1770030540,open:1.18644,high:1.18653,low:1.18635,close:1.18651},
{time:1770030600,open:1.18647,high:1.18657,low:1.18643,close:1.18644},
{time:1770030660,open:1.18644,high:1.18645,low:1.18628,close:1.18628},
{time:1770030720,open:1.18627,high:1.18636,low:1.18624,close:1.18632},
{time:1770030780,open:1.18632,high:1.18645,low:1.18632,close:1.18644},
{time:1770030840,open:1.18646,high:1.18662,low:1.18644,close:1.1865},
{time:1770030900,open:1.18649,high:1.1866,low:1.18629,close:1.1864},
{time:1770030960,open:1.1864,high:1.1865,low:1.18637,close:1.18646},
{time:1770031020,open:1.18644,high:1.18652,low:1.18632,close:1.18632},
{time:1770031080,open:1.18631,high:1.18655,low:1.18631,close:1.18654},
{time:1770031140,open:1.18654,high:1.18656,low:1.18636,close:1.1864},
{time:1770031200,open:1.1864,high:1.1864,low:1.18617,close:1.18621},
{time:1770031260,open:1.18621,high:1.18651,low:1.1862,close:1.18648},
{time:1770031320,open:1.1865,high:1.18652,low:1.18636,close:1.18651},
{time:1770031380,open:1.18651,high:1.18661,low:1.18645,close:1.18649},
{time:1770031440,open:1.18651,high:1.18671,low:1.18646,close:1.18658},
{time:1770031500,open:1.1866,high:1.18661,low:1.1864,close:1.18648},
{time:1770031560,open:1.18647,high:1.18666,low:1.18644,close:1.18663},
{time:1770031620,open:1.18663,high:1.18677,low:1.18663,close:1.18675},
{time:1770031680,open:1.18676,high:1.18681,low:1.18666,close:1.18669},
{time:1770031740,open:1.18666,high:1.18676,low:1.18663,close:1.1867},
{time:1770031800,open:1.18671,high:1.18692,low:1.18661,close:1.18692},
{time:1770031860,open:1.18691,high:1.18691,low:1.18674,close:1.18677},
{time:1770031920,open:1.1868,high:1.18687,low:1.18679,close:1.18681},
{time:1770031980,open:1.18679,high:1.18696,low:1.18677,close:1.18684},
{time:1770032040,open:1.18685,high:1.18698,low:1.18683,close:1.18696},
{time:1770032100,open:1.18697,high:1.18708,low:1.18695,close:1.18702},
{time:1770032160,open:1.18703,high:1.18707,low:1.18696,close:1.18696},
{time:1770032220,open:1.18696,high:1.18712,low:1.18695,close:1.18712},
{time:1770032280,open:1.18712,high:1.18712,low:1.18694,close:1.18694},
{time:1770032340,open:1.18694,high:1.18694,low:1.18646,close:1.18648},
{time:1770032400,open:1.18646,high:1.18646,low:1.18605,close:1.18627},
{time:1770032460,open:1.18626,high:1.1863,low:1.18615,close:1.1862},
{time:1770032520,open:1.18621,high:1.18621,low:1.18606,close:1.1861},
{time:1770032580,open:1.18608,high:1.18622,low:1.18605,close:1.18616},
{time:1770032640,open:1.18619,high:1.18628,low:1.18614,close:1.18622},
{time:1770032700,open:1.18622,high:1.18629,low:1.18608,close:1.18627},
{time:1770032760,open:1.18626,high:1.18628,low:1.18619,close:1.18627},
{time:1770032820,open:1.18629,high:1.18631,low:1.18609,close:1.18609},
{time:1770032880,open:1.18611,high:1.1862,low:1.18609,close:1.18618},
{time:1770032940,open:1.18617,high:1.18625,low:1.1861,close:1.18619},
{time:1770033000,open:1.1862,high:1.18623,low:1.18612,close:1.18619},
{time:1770033060,open:1.18617,high:1.18624,low:1.1861,close:1.18617},
{time:1770033120,open:1.18617,high:1.18624,low:1.1861,close:1.18619},
{time:1770033180,open:1.18617,high:1.1863,low:1.18616,close:1.1863},
{time:1770033240,open:1.18628,high:1.18639,low:1.18621,close:1.18623},
{time:1770033300,open:1.18622,high:1.18643,low:1.18621,close:1.18638},
{time:1770033360,open:1.18636,high:1.18642,low:1.18626,close:1.18633},
{time:1770033420,open:1.18633,high:1.18642,low:1.1863,close:1.18637},
{time:1770033480,open:1.18636,high:1.18651,low:1.18634,close:1.18647},
{time:1770033540,open:1.18648,high:1.18655,low:1.18645,close:1.18646},
{time:1770033600,open:1.18645,high:1.18672,low:1.18631,close:1.18672},
{time:1770033660,open:1.18671,high:1.18687,low:1.18671,close:1.18678},
{time:1770033720,open:1.18679,high:1.18698,low:1.18672,close:1.18692},
{time:1770033780,open:1.18692,high:1.18693,low:1.1866,close:1.18665},
{time:1770033840,open:1.18664,high:1.18671,low:1.18628,close:1.18628},
{time:1770033900,open:1.18626,high:1.18632,low:1.18614,close:1.18617},
{time:1770033960,open:1.18615,high:1.18622,low:1.18609,close:1.18616},
{time:1770034020,open:1.18616,high:1.18616,low:1.1861,close:1.18612},
{time:1770034080,open:1.18613,high:1.1862,low:1.18595,close:1.18596},
{time:1770034140,open:1.18591,high:1.18602,low:1.18585,close:1.18602},
{time:1770034200,open:1.18602,high:1.18607,low:1.18597,close:1.18601},
{time:1770034260,open:1.18603,high:1.18609,low:1.18585,close:1.18598},
{time:1770034320,open:1.18597,high:1.18597,low:1.18561,close:1.18561},
{time:1770034380,open:1.18563,high:1.18571,low:1.18544,close:1.18571},
{time:1770034440,open:1.1857,high:1.18578,low:1.18563,close:1.18568},
{time:1770034500,open:1.18565,high:1.18565,low:1.18547,close:1.18551},
{time:1770034560,open:1.18552,high:1.18567,low:1.1855,close:1.18561},
{time:1770034620,open:1.18561,high:1.18566,low:1.18543,close:1.18545},
{time:1770034680,open:1.18545,high:1.18545,low:1.18516,close:1.18521},
{time:1770034740,open:1.18522,high:1.18525,low:1.18514,close:1.1852},
{time:1770034800,open:1.18523,high:1.18533,low:1.18511,close:1.1852},
{time:1770034860,open:1.18521,high:1.18536,low:1.1852,close:1.1853},
{time:1770034920,open:1.18532,high:1.18549,low:1.18528,close:1.18541},
{time:1770034980,open:1.18542,high:1.18561,low:1.1854,close:1.18559},
{time:1770035040,open:1.18557,high:1.18557,low:1.18537,close:1.18539},
{time:1770035100,open:1.1854,high:1.18546,low:1.18535,close:1.18537},
{time:1770035160,open:1.18537,high:1.18539,low:1.18531,close:1.18537},
{time:1770035220,open:1.18536,high:1.18553,low:1.18535,close:1.18545},
{time:1770035280,open:1.18544,high:1.18551,low:1.1854,close:1.18542},
{time:1770035340,open:1.18543,high:1.1855,low:1.1854,close:1.18548},
{time:1770035400,open:1.18546,high:1.18547,low:1.18527,close:1.18531},
{time:1770035460,open:1.18529,high:1.18553,low:1.18529,close:1.18552},
{time:1770035520,open:1.18551,high:1.18552,low:1.18539,close:1.1854},
{time:1770035580,open:1.18538,high:1.18546,low:1.18512,close:1.18512},
{time:1770035640,open:1.18513,high:1.18537,low:1.18513,close:1.18537},
{time:1770035700,open:1.18536,high:1.18541,low:1.18506,close:1.18506},
{time:1770035760,open:1.18505,high:1.18512,low:1.18489,close:1.18496},
{time:1770035820,open:1.18494,high:1.1851,low:1.18494,close:1.18508},
{time:1770035880,open:1.18508,high:1.18509,low:1.18461,close:1.18465},
{time:1770035940,open:1.18465,high:1.18475,low:1.18456,close:1.18457},
{time:1770036000,open:1.18457,high:1.18476,low:1.18448,close:1.18474},
{time:1770036060,open:1.18478,high:1.18484,low:1.18462,close:1.18463},
{time:1770036120,open:1.1846,high:1.18477,low:1.1845,close:1.18475},
{time:1770036180,open:1.18476,high:1.18491,low:1.1847,close:1.18478},
{time:1770036240,open:1.18476,high:1.18483,low:1.18474,close:1.1848},
{time:1770036300,open:1.18476,high:1.18499,low:1.18475,close:1.18499},
{time:1770036360,open:1.18498,high:1.18506,low:1.18495,close:1.18499},
{time:1770036420,open:1.18501,high:1.18501,low:1.18486,close:1.18496},
{time:1770036480,open:1.18494,high:1.18495,low:1.18479,close:1.1848},
{time:1770036540,open:1.18478,high:1.18478,low:1.18462,close:1.18471},
{time:1770036600,open:1.1847,high:1.18487,low:1.18459,close:1.18464},
{time:1770036660,open:1.18464,high:1.18513,low:1.1846,close:1.18506},
{time:1770036720,open:1.18509,high:1.18513,low:1.18491,close:1.18495},
{time:1770036780,open:1.18497,high:1.18505,low:1.18495,close:1.18497},
{time:1770036840,open:1.18496,high:1.18502,low:1.18488,close:1.18488},
{time:1770036900,open:1.18487,high:1.18487,low:1.18432,close:1.18437},
{time:1770036960,open:1.18439,high:1.18466,low:1.1843,close:1.18454},
{time:1770037020,open:1.18455,high:1.18487,low:1.1845,close:1.18486},
{time:1770037080,open:1.18486,high:1.18497,low:1.18477,close:1.1849},
{time:1770037140,open:1.1849,high:1.185,low:1.18487,close:1.18498},
{time:1770037200,open:1.18498,high:1.18501,low:1.18453,close:1.18461},
{time:1770037260,open:1.18464,high:1.18476,low:1.18447,close:1.18461},
{time:1770037320,open:1.18461,high:1.18461,low:1.18415,close:1.18417},
{time:1770037380,open:1.18416,high:1.18421,low:1.18346,close:1.18364},
{time:1770037440,open:1.18367,high:1.1841,low:1.18357,close:1.1841},
{time:1770037500,open:1.18411,high:1.18415,low:1.18389,close:1.184},
{time:1770037560,open:1.18399,high:1.18405,low:1.1838,close:1.18384},
{time:1770037620,open:1.18386,high:1.18424,low:1.18384,close:1.18409},
{time:1770037680,open:1.18407,high:1.18409,low:1.18391,close:1.18399},
{time:1770037740,open:1.18398,high:1.18405,low:1.18387,close:1.18396},
{time:1770037800,open:1.18398,high:1.1841,low:1.18387,close:1.1841},
{time:1770037860,open:1.18412,high:1.18412,low:1.18381,close:1.18381},
{time:1770037920,open:1.18379,high:1.18392,low:1.18376,close:1.18382},
{time:1770037980,open:1.18382,high:1.18387,low:1.18375,close:1.1838},
{time:1770038040,open:1.1838,high:1.1838,low:1.18341,close:1.18376},
{time:1770038100,open:1.18377,high:1.1838,low:1.18333,close:1.18334},
{time:1770038160,open:1.18334,high:1.18346,low:1.18329,close:1.18331},
{time:1770038220,open:1.18332,high:1.18346,low:1.1833,close:1.18334},
{time:1770038280,open:1.18334,high:1.18354,low:1.18324,close:1.18325},
{time:1770038340,open:1.18325,high:1.18326,low:1.18308,close:1.18317},
{time:1770038400,open:1.18314,high:1.18328,low:1.18303,close:1.18324},
{time:1770038460,open:1.18322,high:1.18333,low:1.18314,close:1.18321},
{time:1770038520,open:1.18323,high:1.18334,low:1.18314,close:1.18329},
{time:1770038580,open:1.18328,high:1.18333,low:1.18314,close:1.1832},
{time:1770038640,open:1.18319,high:1.18331,low:1.18316,close:1.18321},
{time:1770038700,open:1.18318,high:1.18322,low:1.1831,close:1.18319},
{time:1770038760,open:1.1832,high:1.18323,low:1.18312,close:1.18315},
{time:1770038820,open:1.18312,high:1.1833,low:1.18311,close:1.18316},
{time:1770038880,open:1.18317,high:1.18317,low:1.18304,close:1.18307},
{time:1770038940,open:1.18305,high:1.18316,low:1.18302,close:1.18305},
{time:1770039000,open:1.18306,high:1.18311,low:1.18297,close:1.18298},
{time:1770039060,open:1.18299,high:1.18304,low:1.18273,close:1.18273},
{time:1770039120,open:1.18274,high:1.18282,low:1.18249,close:1.18255},
{time:1770039180,open:1.18256,high:1.18267,low:1.18247,close:1.18261},
{time:1770039240,open:1.18261,high:1.18268,low:1.1825,close:1.18257},
{time:1770039300,open:1.18258,high:1.18259,low:1.18209,close:1.1821},
{time:1770039360,open:1.1821,high:1.18246,low:1.18208,close:1.18228},
{time:1770039420,open:1.18229,high:1.18237,low:1.18217,close:1.18226},
{time:1770039480,open:1.18224,high:1.18228,low:1.18208,close:1.18215},
{time:1770039540,open:1.18215,high:1.18215,low:1.18198,close:1.18206},
{time:1770039600,open:1.18206,high:1.18211,low:1.18195,close:1.18204},
{time:1770039660,open:1.18204,high:1.18226,low:1.18204,close:1.18226},
{time:1770039720,open:1.18226,high:1.1823,low:1.18217,close:1.18221},
{time:1770039780,open:1.18222,high:1.18232,low:1.18217,close:1.18222},
{time:1770039840,open:1.18222,high:1.18235,low:1.18219,close:1.18235},
{time:1770039900,open:1.18236,high:1.18243,low:1.1821,close:1.1821},
{time:1770039960,open:1.1821,high:1.18224,low:1.18202,close:1.1822},
{time:1770040020,open:1.18223,high:1.18255,low:1.18222,close:1.18254},
{time:1770040080,open:1.18253,high:1.18265,low:1.18247,close:1.18257},
{time:1770040140,open:1.18258,high:1.18262,low:1.18242,close:1.18251},
{time:1770040200,open:1.18248,high:1.18267,low:1.18242,close:1.1826},
{time:1770040260,open:1.18259,high:1.18259,low:1.18247,close:1.1825},
{time:1770040320,open:1.18248,high:1.18248,low:1.18236,close:1.18242},
{time:1770040380,open:1.1824,high:1.18252,low:1.18234,close:1.18247},
{time:1770040440,open:1.18245,high:1.18251,low:1.18233,close:1.18237},
{time:1770040500,open:1.18238,high:1.18248,low:1.18237,close:1.18243},
{time:1770040560,open:1.18243,high:1.18251,low:1.18237,close:1.18242},
{time:1770040620,open:1.18242,high:1.18251,low:1.182,close:1.18211},
{time:1770040680,open:1.18208,high:1.18208,low:1.18178,close:1.18186},
{time:1770040740,open:1.18187,high:1.18219,low:1.1818,close:1.18208},
{time:1770040800,open:1.18206,high:1.18214,low:1.18196,close:1.18207},
{time:1770040860,open:1.18206,high:1.18206,low:1.18182,close:1.1819},
{time:1770040920,open:1.1819,high:1.182,low:1.18177,close:1.18191},
{time:1770040980,open:1.1819,high:1.18192,low:1.18176,close:1.18182},
{time:1770041040,open:1.18181,high:1.182,low:1.18178,close:1.18192},
{time:1770041100,open:1.18193,high:1.18195,low:1.1818,close:1.18192},
{time:1770041160,open:1.18191,high:1.18195,low:1.18184,close:1.18186},
{time:1770041220,open:1.18188,high:1.18198,low:1.18169,close:1.18181},
{time:1770041280,open:1.18182,high:1.18192,low:1.18179,close:1.18181},
{time:1770041340,open:1.18182,high:1.18191,low:1.1813,close:1.18143},
{time:1770041400,open:1.18141,high:1.1815,low:1.18132,close:1.18135},
{time:1770041460,open:1.18134,high:1.18145,low:1.18122,close:1.18122},
{time:1770041520,open:1.18123,high:1.18126,low:1.18112,close:1.18112},
{time:1770041580,open:1.18113,high:1.18131,low:1.18112,close:1.18112},
{time:1770041640,open:1.18112,high:1.18112,low:1.18078,close:1.18084},
{time:1770041700,open:1.18083,high:1.18098,low:1.18077,close:1.18086},
{time:1770041760,open:1.18088,high:1.18102,low:1.18077,close:1.18092},
{time:1770041820,open:1.18093,high:1.18098,low:1.18079,close:1.18079},
{time:1770041880,open:1.18078,high:1.18091,low:1.18077,close:1.18086},
{time:1770041940,open:1.18087,high:1.18088,low:1.18053,close:1.18067},
{time:1770042000,open:1.18068,high:1.18083,low:1.18062,close:1.18078},
{time:1770042060,open:1.18077,high:1.18077,low:1.18053,close:1.18057},
{time:1770042120,open:1.18058,high:1.18088,low:1.18056,close:1.18087},
{time:1770042180,open:1.18087,high:1.18087,low:1.18067,close:1.1807},
{time:1770042240,open:1.18072,high:1.18086,low:1.18069,close:1.18078},
{time:1770042300,open:1.18076,high:1.18094,low:1.18076,close:1.18091},
{time:1770042360,open:1.18091,high:1.18116,low:1.18089,close:1.18113},
{time:1770042420,open:1.18116,high:1.18124,low:1.18108,close:1.1812},
{time:1770042480,open:1.1812,high:1.18138,low:1.18119,close:1.18126},
{time:1770042540,open:1.18125,high:1.18138,low:1.18117,close:1.18126},
{time:1770042600,open:1.18128,high:1.1815,low:1.18103,close:1.18149},
{time:1770042660,open:1.18149,high:1.18162,low:1.18138,close:1.18158},
{time:1770042720,open:1.18159,high:1.18197,low:1.18151,close:1.18194},
{time:1770042780,open:1.18194,high:1.18209,low:1.18183,close:1.18183},
{time:1770042840,open:1.1818,high:1.18197,low:1.18178,close:1.18185},
{time:1770042900,open:1.18187,high:1.18218,low:1.18175,close:1.18212},
{time:1770042960,open:1.1821,high:1.18232,low:1.18207,close:1.18222},
{time:1770043020,open:1.18223,high:1.18252,low:1.18211,close:1.18247},
{time:1770043080,open:1.18246,high:1.18257,low:1.1823,close:1.18236},
{time:1770043140,open:1.18235,high:1.18237,low:1.18207,close:1.18225},
{time:1770043200,open:1.18224,high:1.18229,low:1.182,close:1.18202},
{time:1770043260,open:1.18198,high:1.18209,low:1.18185,close:1.18204},
{time:1770043320,open:1.18204,high:1.18216,low:1.18201,close:1.18213},
{time:1770043380,open:1.18212,high:1.18243,low:1.18212,close:1.18233},
{time:1770043440,open:1.18233,high:1.18254,low:1.18231,close:1.18232},
{time:1770043500,open:1.18233,high:1.18262,low:1.18233,close:1.18257},
{time:1770043560,open:1.18261,high:1.18282,low:1.18243,close:1.18261},
{time:1770043620,open:1.1826,high:1.18277,low:1.18253,close:1.18274},
{time:1770043680,open:1.18273,high:1.18278,low:1.18258,close:1.1827},
{time:1770043740,open:1.18269,high:1.18269,low:1.1823,close:1.18231},
{time:1770043800,open:1.18231,high:1.18244,low:1.18221,close:1.18239},
{time:1770043860,open:1.18238,high:1.1827,low:1.18237,close:1.18261},
{time:1770043920,open:1.18259,high:1.18288,low:1.18244,close:1.18278},
{time:1770043980,open:1.18278,high:1.18298,low:1.18272,close:1.18288},
{time:1770044040,open:1.18287,high:1.18293,low:1.18271,close:1.18276},
{time:1770044100,open:1.18278,high:1.18283,low:1.18246,close:1.18247},
{time:1770044160,open:1.18246,high:1.18247,low:1.18223,close:1.1823},
{time:1770044220,open:1.18235,high:1.18245,low:1.18227,close:1.1823},
{time:1770044280,open:1.18228,high:1.18244,low:1.18222,close:1.18224},
{time:1770044340,open:1.18224,high:1.18242,low:1.1821,close:1.18211},
{time:1770044400,open:1.18212,high:1.18212,low:1.18069,close:1.1812},
{time:1770044460,open:1.18121,high:1.18147,low:1.1809,close:1.18097},
{time:1770044520,open:1.18098,high:1.18105,low:1.18049,close:1.18055},
{time:1770044580,open:1.18051,high:1.18167,low:1.18051,close:1.18136},
{time:1770044640,open:1.18137,high:1.18161,low:1.18128,close:1.18141},
{time:1770044700,open:1.18141,high:1.18149,low:1.18105,close:1.1811},
{time:1770044760,open:1.18108,high:1.18147,low:1.18106,close:1.18136},
{time:1770044820,open:1.18133,high:1.1814,low:1.18111,close:1.18134},
{time:1770044880,open:1.18132,high:1.1814,low:1.18117,close:1.18135},
{time:1770044940,open:1.18135,high:1.18167,low:1.18114,close:1.18167},
{time:1770045000,open:1.18165,high:1.18165,low:1.18116,close:1.1812},
{time:1770045060,open:1.18121,high:1.18149,low:1.18109,close:1.18109},
{time:1770045120,open:1.18112,high:1.18114,low:1.18071,close:1.18071},
{time:1770045180,open:1.18073,high:1.18076,low:1.18031,close:1.18033},
{time:1770045240,open:1.18035,high:1.18078,low:1.18033,close:1.18065},
{time:1770045300,open:1.18068,high:1.18101,low:1.18059,close:1.18073},
{time:1770045360,open:1.18077,high:1.18079,low:1.18045,close:1.18055},
{time:1770045420,open:1.18057,high:1.18062,low:1.18023,close:1.18025},
{time:1770045480,open:1.18024,high:1.18028,low:1.18004,close:1.18008},
{time:1770045540,open:1.18007,high:1.18067,low:1.18007,close:1.18044},
{time:1770045600,open:1.18044,high:1.18068,low:1.18032,close:1.18059},
{time:1770045660,open:1.18061,high:1.18111,low:1.18051,close:1.18111},
{time:1770045720,open:1.18113,high:1.18116,low:1.1809,close:1.18102},
{time:1770045780,open:1.18103,high:1.18136,low:1.18103,close:1.18136},
{time:1770045840,open:1.18135,high:1.18138,low:1.18117,close:1.18122},
{time:1770045900,open:1.18119,high:1.18142,low:1.18105,close:1.18109},
{time:1770045960,open:1.18114,high:1.1812,low:1.18098,close:1.18099},
{time:1770046020,open:1.181,high:1.181,low:1.18062,close:1.18063},
{time:1770046080,open:1.1806,high:1.18061,low:1.18021,close:1.18029},
{time:1770046140,open:1.18032,high:1.18061,low:1.1802,close:1.18041},
{time:1770046200,open:1.18041,high:1.18051,low:1.18009,close:1.18032},
{time:1770046260,open:1.18034,high:1.18045,low:1.18014,close:1.18021},
{time:1770046320,open:1.18021,high:1.18046,low:1.18012,close:1.18036},
{time:1770046380,open:1.18034,high:1.18038,low:1.18017,close:1.18019},
{time:1770046440,open:1.18021,high:1.1805,low:1.18017,close:1.18023},
{time:1770046500,open:1.18023,high:1.18061,low:1.18023,close:1.18057},
{time:1770046560,open:1.18055,high:1.1808,low:1.18037,close:1.1808},
{time:1770046620,open:1.18082,high:1.18082,low:1.18005,close:1.18006},
{time:1770046680,open:1.18005,high:1.18028,low:1.18005,close:1.18018},
{time:1770046740,open:1.18017,high:1.18036,low:1.18006,close:1.18036},
{time:1770046800,open:1.1804,high:1.18049,low:1.18007,close:1.18008},
{time:1770046860,open:1.1801,high:1.18035,low:1.17997,close:1.18017},
{time:1770046920,open:1.18019,high:1.18025,low:1.17935,close:1.17936},
{time:1770046980,open:1.17937,high:1.17966,low:1.17936,close:1.17959},
{time:1770047040,open:1.17961,high:1.17961,low:1.17917,close:1.17927},
{time:1770047100,open:1.17927,high:1.17946,low:1.17918,close:1.17936},
{time:1770047160,open:1.17934,high:1.17942,low:1.17915,close:1.17931},
{time:1770047220,open:1.17931,high:1.17936,low:1.1791,close:1.17928},
{time:1770047280,open:1.17926,high:1.17949,low:1.17923,close:1.1793},
{time:1770047340,open:1.1793,high:1.17954,low:1.17916,close:1.17922},
{time:1770047400,open:1.17922,high:1.17932,low:1.1791,close:1.17922},
{time:1770047460,open:1.17922,high:1.17964,low:1.17922,close:1.17945},
{time:1770047520,open:1.17945,high:1.17951,low:1.17931,close:1.17939},
{time:1770047580,open:1.17937,high:1.17946,low:1.1791,close:1.1792},
{time:1770047640,open:1.17918,high:1.17944,low:1.17918,close:1.17939},
{time:1770047700,open:1.1794,high:1.17953,low:1.17933,close:1.17948},
{time:1770047760,open:1.17946,high:1.17951,low:1.17917,close:1.17949},
{time:1770047820,open:1.1795,high:1.1795,low:1.17903,close:1.17912},
{time:1770047880,open:1.17913,high:1.17917,low:1.17902,close:1.1791},
{time:1770047940,open:1.17912,high:1.17926,low:1.17911,close:1.17923},
{time:1770048000,open:1.17922,high:1.17932,low:1.17919,close:1.17929},
{time:1770048060,open:1.17928,high:1.17936,low:1.17915,close:1.17932},
{time:1770048120,open:1.17934,high:1.17934,low:1.17905,close:1.17908},
{time:1770048180,open:1.1791,high:1.17963,low:1.17903,close:1.1792},
{time:1770048240,open:1.17923,high:1.17941,low:1.17916,close:1.17931},
{time:1770048300,open:1.17935,high:1.17937,low:1.17918,close:1.17935},
{time:1770048360,open:1.17936,high:1.17962,low:1.17936,close:1.17962},
{time:1770048420,open:1.17962,high:1.17993,low:1.17959,close:1.17993},
{time:1770048480,open:1.17993,high:1.18023,low:1.17991,close:1.18016},
{time:1770048540,open:1.18018,high:1.18023,low:1.18004,close:1.18017},
{time:1770048600,open:1.18017,high:1.18017,low:1.1799,close:1.17997},
{time:1770048660,open:1.17997,high:1.18017,low:1.17997,close:1.18015},
{time:1770048720,open:1.18013,high:1.18041,low:1.18007,close:1.18038},
{time:1770048780,open:1.18037,high:1.18047,low:1.1803,close:1.18046},
{time:1770048840,open:1.18046,high:1.18053,low:1.18036,close:1.18038},
{time:1770048900,open:1.18039,high:1.18044,low:1.18019,close:1.18024},
{time:1770048960,open:1.18024,high:1.18038,low:1.18022,close:1.18037},
{time:1770049020,open:1.18038,high:1.18052,low:1.18031,close:1.18043},
{time:1770049080,open:1.18043,high:1.1805,low:1.18022,close:1.18038},
{time:1770049140,open:1.18038,high:1.18064,low:1.18038,close:1.18056},
{time:1770049200,open:1.18055,high:1.18059,low:1.18026,close:1.18057},
{time:1770049260,open:1.18059,high:1.18066,low:1.18053,close:1.18053},
{time:1770049320,open:1.1805,high:1.18062,low:1.18045,close:1.18061},
{time:1770049380,open:1.1806,high:1.1806,low:1.1803,close:1.18042},
{time:1770049440,open:1.18042,high:1.18058,low:1.18023,close:1.18027},
{time:1770049500,open:1.18028,high:1.18033,low:1.18014,close:1.18022},
{time:1770049560,open:1.18021,high:1.18021,low:1.17994,close:1.17996},
{time:1770049620,open:1.17995,high:1.1802,low:1.17988,close:1.18018},
{time:1770049680,open:1.18019,high:1.18037,low:1.18006,close:1.18034},
{time:1770049740,open:1.18034,high:1.1805,low:1.18023,close:1.18023},
{time:1770049800,open:1.18023,high:1.18058,low:1.18018,close:1.18047},
{time:1770049860,open:1.18046,high:1.18047,low:1.18016,close:1.18017},
{time:1770049920,open:1.18017,high:1.18019,low:1.17997,close:1.18009},
{time:1770049980,open:1.18006,high:1.18038,low:1.18003,close:1.18034},
{time:1770050040,open:1.1803,high:1.18049,low:1.18025,close:1.18049},
{time:1770050100,open:1.1805,high:1.18063,low:1.18045,close:1.18059},
{time:1770050160,open:1.18057,high:1.18059,low:1.18033,close:1.18038},
{time:1770050220,open:1.18038,high:1.18057,low:1.18034,close:1.18057},
{time:1770050280,open:1.18057,high:1.18068,low:1.18046,close:1.1805},
{time:1770050340,open:1.18049,high:1.18057,low:1.18039,close:1.1805},
{time:1770050400,open:1.1805,high:1.18057,low:1.18038,close:1.18044},
{time:1770050460,open:1.18046,high:1.18064,low:1.18046,close:1.18058},
{time:1770050520,open:1.18057,high:1.18066,low:1.18052,close:1.18062},
{time:1770050580,open:1.18063,high:1.18069,low:1.18059,close:1.18065},
{time:1770050640,open:1.18063,high:1.18067,low:1.18054,close:1.18062},
{time:1770050700,open:1.18065,high:1.18068,low:1.18022,close:1.18023},
{time:1770050760,open:1.18022,high:1.18023,low:1.17999,close:1.18003},
{time:1770050820,open:1.18004,high:1.18017,low:1.18002,close:1.18006},
{time:1770050880,open:1.18008,high:1.18018,low:1.17987,close:1.17993},
{time:1770050940,open:1.17993,high:1.18001,low:1.17987,close:1.17999},
{time:1770051000,open:1.17997,high:1.18014,low:1.17997,close:1.18003},
{time:1770051060,open:1.18002,high:1.18016,low:1.17993,close:1.17998},
{time:1770051120,open:1.17998,high:1.18001,low:1.17984,close:1.17985},
{time:1770051180,open:1.17986,high:1.18012,low:1.17986,close:1.1801},
{time:1770051240,open:1.1801,high:1.18018,low:1.17993,close:1.17993},
{time:1770051300,open:1.17992,high:1.1801,low:1.17992,close:1.17998},
{time:1770051360,open:1.17997,high:1.18034,low:1.17997,close:1.18033},
{time:1770051420,open:1.18035,high:1.18057,low:1.18018,close:1.18049},
{time:1770051480,open:1.18047,high:1.18077,low:1.18047,close:1.18047},
{time:1770051540,open:1.18046,high:1.18065,low:1.18046,close:1.18062},
{time:1770051600,open:1.18064,high:1.18086,low:1.18058,close:1.18083},
{time:1770051660,open:1.18084,high:1.18107,low:1.18082,close:1.18088},
{time:1770051720,open:1.18089,high:1.18108,low:1.18086,close:1.18087},
{time:1770051780,open:1.18088,high:1.18106,low:1.18087,close:1.181},
{time:1770051840,open:1.18101,high:1.18105,low:1.18095,close:1.181},
{time:1770051900,open:1.18098,high:1.18098,low:1.1808,close:1.18083},
{time:1770051960,open:1.18082,high:1.18099,low:1.18082,close:1.18089},
{time:1770052020,open:1.1809,high:1.18113,low:1.18088,close:1.18111},
{time:1770052080,open:1.18112,high:1.18116,low:1.18107,close:1.18112},
{time:1770052140,open:1.18111,high:1.18121,low:1.18106,close:1.18118},
{time:1770052200,open:1.18121,high:1.18121,low:1.18106,close:1.18111},
{time:1770052260,open:1.18111,high:1.18113,low:1.18092,close:1.18104},
{time:1770052320,open:1.18105,high:1.18111,low:1.18101,close:1.1811},
{time:1770052380,open:1.18109,high:1.18111,low:1.181,close:1.18107},
{time:1770052440,open:1.18107,high:1.18123,low:1.18106,close:1.1812},
{time:1770052500,open:1.18121,high:1.18124,low:1.1811,close:1.18112},
{time:1770052560,open:1.18111,high:1.18122,low:1.18108,close:1.18108},
{time:1770052620,open:1.18106,high:1.18107,low:1.18071,close:1.18084},
{time:1770052680,open:1.18084,high:1.18093,low:1.18072,close:1.18075},
{time:1770052740,open:1.18077,high:1.18087,low:1.18075,close:1.18075},
{time:1770052800,open:1.18075,high:1.1808,low:1.18063,close:1.18073},
{time:1770052860,open:1.18074,high:1.18077,low:1.18049,close:1.18049},
{time:1770052920,open:1.1805,high:1.18055,low:1.18034,close:1.18036},
{time:1770052980,open:1.18035,high:1.1804,low:1.18033,close:1.18037},
{time:1770053040,open:1.18037,high:1.1804,low:1.1802,close:1.18037},
{time:1770053100,open:1.18038,high:1.18038,low:1.1802,close:1.1802},
{time:1770053160,open:1.1802,high:1.18053,low:1.18018,close:1.18042},
{time:1770053220,open:1.18039,high:1.18051,low:1.18036,close:1.18051},
{time:1770053280,open:1.18051,high:1.18053,low:1.18033,close:1.18035},
{time:1770053340,open:1.18034,high:1.18043,low:1.18022,close:1.18042},
{time:1770053400,open:1.18041,high:1.18041,low:1.18019,close:1.1802},
{time:1770053460,open:1.18019,high:1.18021,low:1.18002,close:1.18007},
{time:1770053520,open:1.18006,high:1.18021,low:1.17991,close:1.18021},
{time:1770053580,open:1.18019,high:1.18048,low:1.18019,close:1.18047},
{time:1770053640,open:1.18048,high:1.18049,low:1.18011,close:1.18011},
{time:1770053700,open:1.18014,high:1.18014,low:1.17997,close:1.17998},
{time:1770053760,open:1.17998,high:1.1801,low:1.17975,close:1.17985},
{time:1770053820,open:1.17986,high:1.17988,low:1.17948,close:1.17957},
{time:1770053880,open:1.17961,high:1.17969,low:1.17949,close:1.17961},
{time:1770053940,open:1.17962,high:1.17981,low:1.17962,close:1.17981},
{time:1770054000,open:1.17982,high:1.18004,low:1.17978,close:1.18004},
{time:1770054060,open:1.18004,high:1.18033,low:1.18004,close:1.1803},
{time:1770054120,open:1.18029,high:1.18035,low:1.18009,close:1.18014},
{time:1770054180,open:1.18016,high:1.18029,low:1.18011,close:1.18017},
{time:1770054240,open:1.18017,high:1.18044,low:1.18017,close:1.18034},
{time:1770054300,open:1.18035,high:1.18053,low:1.18035,close:1.18049},
{time:1770054360,open:1.18051,high:1.18055,low:1.18032,close:1.18034},
{time:1770054420,open:1.18034,high:1.18036,low:1.18003,close:1.18015},
{time:1770054480,open:1.18016,high:1.18055,low:1.18016,close:1.18052},
{time:1770054540,open:1.18051,high:1.18059,low:1.18039,close:1.18058},
{time:1770054600,open:1.18056,high:1.18059,low:1.18037,close:1.1804},
{time:1770054660,open:1.18039,high:1.18039,low:1.17999,close:1.18015},
{time:1770054720,open:1.18013,high:1.18016,low:1.17976,close:1.17986},
{time:1770054780,open:1.17987,high:1.17994,low:1.17976,close:1.17985},
{time:1770054840,open:1.17987,high:1.18001,low:1.17986,close:1.17991},
{time:1770054900,open:1.17989,high:1.18026,low:1.17985,close:1.18024},
{time:1770054960,open:1.18023,high:1.18023,low:1.17981,close:1.17983},
{time:1770055020,open:1.17983,high:1.17992,low:1.1797,close:1.1797},
{time:1770055080,open:1.17968,high:1.1799,low:1.17964,close:1.17984},
{time:1770055140,open:1.17986,high:1.18002,low:1.17985,close:1.17999},
{time:1770055200,open:1.18003,high:1.1801,low:1.17993,close:1.18002},
{time:1770055260,open:1.18001,high:1.18006,low:1.17978,close:1.1798},
{time:1770055320,open:1.17977,high:1.17986,low:1.17921,close:1.17927},
{time:1770055380,open:1.17927,high:1.17934,low:1.17917,close:1.17917},
{time:1770055440,open:1.17918,high:1.1792,low:1.17911,close:1.17916},
{time:1770055500,open:1.17915,high:1.17917,low:1.17901,close:1.17905},
{time:1770055560,open:1.17907,high:1.17921,low:1.17907,close:1.17917},
{time:1770055620,open:1.17917,high:1.17917,low:1.17853,close:1.17875},
{time:1770055680,open:1.17876,high:1.17905,low:1.17876,close:1.17885},
{time:1770055740,open:1.17886,high:1.1789,low:1.17863,close:1.17888},
{time:1770055800,open:1.17887,high:1.1789,low:1.1787,close:1.17884},
{time:1770055860,open:1.17886,high:1.1789,low:1.17879,close:1.17888},
{time:1770055920,open:1.17887,high:1.17887,low:1.17855,close:1.17856},
{time:1770055980,open:1.17853,high:1.17856,low:1.17839,close:1.17842},
{time:1770056040,open:1.17841,high:1.17841,low:1.17821,close:1.17828},
{time:1770056100,open:1.17828,high:1.17856,low:1.17824,close:1.17851},
{time:1770056160,open:1.17849,high:1.1785,low:1.17817,close:1.17848},
{time:1770056220,open:1.17847,high:1.17848,low:1.17827,close:1.17844},
{time:1770056280,open:1.17846,high:1.17849,low:1.17834,close:1.17843},
{time:1770056340,open:1.17843,high:1.17843,low:1.17797,close:1.178},
{time:1770056400,open:1.17802,high:1.1783,low:1.17798,close:1.17806},
{time:1770056460,open:1.17808,high:1.17825,low:1.17808,close:1.17823},
{time:1770056520,open:1.17824,high:1.17867,low:1.17824,close:1.17866},
{time:1770056580,open:1.17872,high:1.17878,low:1.17857,close:1.17866},
{time:1770056640,open:1.17864,high:1.17882,low:1.1786,close:1.17876},
{time:1770056700,open:1.17877,high:1.17882,low:1.17865,close:1.17867},
{time:1770056760,open:1.17868,high:1.17882,low:1.17861,close:1.17881},
{time:1770056820,open:1.17881,high:1.17883,low:1.17867,close:1.17867},
{time:1770056880,open:1.17871,high:1.17872,low:1.17856,close:1.17871},
{time:1770056940,open:1.17874,high:1.17882,low:1.17865,close:1.17871},
{time:1770057000,open:1.17871,high:1.17905,low:1.17871,close:1.17905},
{time:1770057060,open:1.17906,high:1.17911,low:1.17902,close:1.17905},
{time:1770057120,open:1.17903,high:1.17906,low:1.17896,close:1.17897},
{time:1770057180,open:1.17897,high:1.17901,low:1.17876,close:1.17879},
{time:1770057240,open:1.17877,high:1.17897,low:1.17877,close:1.17889},
{time:1770057300,open:1.17889,high:1.17905,low:1.17876,close:1.17877},
{time:1770057360,open:1.17877,high:1.17879,low:1.17864,close:1.17877},
{time:1770057420,open:1.17878,high:1.17892,low:1.1787,close:1.17892},
{time:1770057480,open:1.1789,high:1.17891,low:1.17849,close:1.17849},
{time:1770057540,open:1.1785,high:1.17858,low:1.17843,close:1.17853},
{time:1770057600,open:1.17853,high:1.1788,low:1.17852,close:1.17877},
{time:1770057660,open:1.17877,high:1.17878,low:1.17866,close:1.17876},
{time:1770057720,open:1.17877,high:1.17883,low:1.17876,close:1.17881},
{time:1770057780,open:1.17881,high:1.17887,low:1.17876,close:1.17887},
{time:1770057840,open:1.17886,high:1.17886,low:1.17868,close:1.17871},
{time:1770057900,open:1.17872,high:1.17882,low:1.17867,close:1.17876},
{time:1770057960,open:1.17877,high:1.17878,low:1.17865,close:1.17868},
{time:1770058020,open:1.1787,high:1.17883,low:1.17864,close:1.17883},
{time:1770058080,open:1.17881,high:1.17882,low:1.17864,close:1.17869},
{time:1770058140,open:1.17869,high:1.1788,low:1.17858,close:1.17866},
{time:1770058200,open:1.17869,high:1.17877,low:1.17855,close:1.17856},
{time:1770058260,open:1.17856,high:1.17867,low:1.17828,close:1.17828},
{time:1770058320,open:1.17827,high:1.17832,low:1.17816,close:1.17819},
{time:1770058380,open:1.17822,high:1.1783,low:1.17805,close:1.17807},
{time:1770058440,open:1.17805,high:1.1781,low:1.17776,close:1.17789},
{time:1770058500,open:1.17791,high:1.178,low:1.17786,close:1.17796},
{time:1770058560,open:1.17795,high:1.17795,low:1.17786,close:1.1779},
{time:1770058620,open:1.17791,high:1.17793,low:1.17779,close:1.17791},
{time:1770058680,open:1.17791,high:1.17795,low:1.17791,close:1.17793},
{time:1770058740,open:1.17795,high:1.17801,low:1.1779,close:1.17797},
{time:1770058800,open:1.17796,high:1.17808,low:1.17791,close:1.17791},
{time:1770058860,open:1.17789,high:1.17794,low:1.17767,close:1.17769},
{time:1770058920,open:1.17767,high:1.17772,low:1.17763,close:1.17771},
{time:1770058980,open:1.17771,high:1.17782,low:1.17766,close:1.17775},
{time:1770059040,open:1.17773,high:1.17791,low:1.17773,close:1.17782},
{time:1770059100,open:1.17781,high:1.17781,low:1.17762,close:1.17767},
{time:1770059160,open:1.17765,high:1.17794,low:1.17763,close:1.17784},
{time:1770059220,open:1.17786,high:1.17786,low:1.1777,close:1.17771},
{time:1770059280,open:1.17771,high:1.17778,low:1.1777,close:1.17776},
{time:1770059340,open:1.17778,high:1.17779,low:1.17759,close:1.17759},
{time:1770059400,open:1.17762,high:1.17766,low:1.17758,close:1.17764},
{time:1770059460,open:1.17766,high:1.17788,low:1.17766,close:1.17783},
{time:1770059520,open:1.17782,high:1.17788,low:1.17771,close:1.17771},
{time:1770059580,open:1.17767,high:1.17777,low:1.17764,close:1.17777},
{time:1770059640,open:1.17778,high:1.17784,low:1.17771,close:1.17773},
{time:1770059700,open:1.17773,high:1.17778,low:1.17764,close:1.17771},
{time:1770059760,open:1.1777,high:1.17778,low:1.17765,close:1.17778},
{time:1770059820,open:1.17778,high:1.17778,low:1.17761,close:1.17768},
{time:1770059880,open:1.1777,high:1.17773,low:1.17767,close:1.17768},
{time:1770059940,open:1.17768,high:1.17776,low:1.17768,close:1.17772},
{time:1770060000,open:1.17772,high:1.17772,low:1.17761,close:1.17764},
{time:1770060060,open:1.17764,high:1.17773,low:1.17764,close:1.17769},
{time:1770060120,open:1.17769,high:1.17773,low:1.17767,close:1.17772},
{time:1770060180,open:1.17773,high:1.17773,low:1.17763,close:1.17769},
{time:1770060240,open:1.1777,high:1.17771,low:1.17764,close:1.17768},
{time:1770060300,open:1.17767,high:1.17781,low:1.17764,close:1.1778},
{time:1770060360,open:1.1778,high:1.17787,low:1.17771,close:1.17778},
{time:1770060420,open:1.17778,high:1.1778,low:1.17772,close:1.17778},
{time:1770060480,open:1.17779,high:1.1778,low:1.17769,close:1.17778},
{time:1770060540,open:1.17779,high:1.17789,low:1.17778,close:1.1778},
{time:1770060600,open:1.17778,high:1.1778,low:1.17772,close:1.17772},
{time:1770060660,open:1.17773,high:1.17773,low:1.17763,close:1.17769},
{time:1770060720,open:1.17771,high:1.17776,low:1.1777,close:1.17774},
{time:1770060780,open:1.17775,high:1.17776,low:1.17773,close:1.17774},
{time:1770060840,open:1.17773,high:1.17775,low:1.17768,close:1.17771},
{time:1770060900,open:1.17772,high:1.17774,low:1.17763,close:1.1777},
{time:1770060960,open:1.17769,high:1.17774,low:1.17769,close:1.17773},
{time:1770061020,open:1.17773,high:1.17774,low:1.17763,close:1.17772},
{time:1770061080,open:1.17774,high:1.17791,low:1.17771,close:1.17789},
{time:1770061140,open:1.1779,high:1.17801,low:1.17787,close:1.17787},
{time:1770061200,open:1.17784,high:1.17817,low:1.17779,close:1.17817},
{time:1770061260,open:1.17818,high:1.17832,low:1.17818,close:1.17828},
{time:1770061320,open:1.17829,high:1.17869,low:1.17828,close:1.17864},
{time:1770061380,open:1.17865,high:1.17873,low:1.17863,close:1.17867},
{time:1770061440,open:1.1787,high:1.17883,low:1.17868,close:1.17882},
{time:1770061500,open:1.17881,high:1.17885,low:1.17873,close:1.17877},
{time:1770061560,open:1.17875,high:1.17875,low:1.17853,close:1.17853},
{time:1770061620,open:1.17856,high:1.17858,low:1.17851,close:1.17853},
{time:1770061680,open:1.17852,high:1.1787,low:1.17849,close:1.17868},
{time:1770061740,open:1.17867,high:1.17868,low:1.1785,close:1.17856},
{time:1770061800,open:1.17857,high:1.17873,low:1.17856,close:1.17867},
{time:1770061860,open:1.17868,high:1.17882,low:1.17866,close:1.17868},
{time:1770061920,open:1.17867,high:1.17884,low:1.17867,close:1.17882},
{time:1770061980,open:1.17881,high:1.17887,low:1.1787,close:1.17872},
{time:1770062040,open:1.1787,high:1.1789,low:1.17858,close:1.17888},
{time:1770062100,open:1.17887,high:1.17903,low:1.17887,close:1.17898},
{time:1770062160,open:1.17895,high:1.17903,low:1.17891,close:1.17899},
{time:1770062220,open:1.179,high:1.17909,low:1.17896,close:1.17907},
{time:1770062280,open:1.17907,high:1.17914,low:1.17886,close:1.17896},
{time:1770062340,open:1.179,high:1.17903,low:1.17877,close:1.17886},
{time:1770062400,open:1.17888,high:1.17888,low:1.17846,close:1.17868},
{time:1770062460,open:1.17868,high:1.17884,low:1.17862,close:1.17883},
{time:1770062520,open:1.17882,high:1.17884,low:1.17866,close:1.17867},
{time:1770062580,open:1.17867,high:1.1787,low:1.17861,close:1.17868},
{time:1770062640,open:1.17864,high:1.17864,low:1.17847,close:1.1785},
{time:1770062700,open:1.17851,high:1.17861,low:1.17849,close:1.1786},
{time:1770062760,open:1.17861,high:1.17888,low:1.17861,close:1.17888},
{time:1770062820,open:1.17888,high:1.17888,low:1.17871,close:1.17878},
{time:1770062880,open:1.1788,high:1.1789,low:1.17877,close:1.17889},
{time:1770062940,open:1.1789,high:1.17902,low:1.1789,close:1.17899},
{time:1770063000,open:1.17898,high:1.17898,low:1.17876,close:1.17882},
{time:1770063060,open:1.17879,high:1.17893,low:1.17877,close:1.17886},
{time:1770063120,open:1.17885,high:1.17888,low:1.17877,close:1.17879},
{time:1770063180,open:1.17881,high:1.17887,low:1.17874,close:1.17875},
{time:1770063240,open:1.17876,high:1.17876,low:1.17859,close:1.1786},
{time:1770063300,open:1.17858,high:1.17858,low:1.17844,close:1.17846},
{time:1770063360,open:1.17846,high:1.17848,low:1.17828,close:1.1783},
{time:1770063420,open:1.1783,high:1.17837,low:1.1783,close:1.17836},
{time:1770063480,open:1.17835,high:1.17836,low:1.1782,close:1.17823},
{time:1770063540,open:1.17822,high:1.17822,low:1.17802,close:1.17812},
{time:1770063600,open:1.17812,high:1.1783,low:1.17812,close:1.17827},
{time:1770063660,open:1.17827,high:1.17827,low:1.17813,close:1.17818},
{time:1770063720,open:1.17816,high:1.17821,low:1.17812,close:1.17818},
{time:1770063780,open:1.17817,high:1.17829,low:1.17812,close:1.17828},
{time:1770063840,open:1.17826,high:1.17829,low:1.1782,close:1.17823},
{time:1770063900,open:1.17822,high:1.17824,low:1.17808,close:1.17818},
{time:1770063960,open:1.17819,high:1.17838,low:1.17819,close:1.17835},
{time:1770064020,open:1.17837,high:1.1784,low:1.17834,close:1.17839},
{time:1770064080,open:1.1784,high:1.17842,low:1.17835,close:1.17836},
{time:1770064140,open:1.17838,high:1.17841,low:1.17834,close:1.17837},
{time:1770064200,open:1.17837,high:1.1784,low:1.17821,close:1.17821},
{time:1770064260,open:1.17823,high:1.17827,low:1.17812,close:1.17823},
{time:1770064320,open:1.17823,high:1.17833,low:1.17821,close:1.17828},
{time:1770064380,open:1.17827,high:1.17833,low:1.17821,close:1.17821},
{time:1770064440,open:1.1782,high:1.17828,low:1.1782,close:1.17822},
{time:1770064500,open:1.17822,high:1.17832,low:1.17822,close:1.17827},
{time:1770064560,open:1.17825,high:1.17847,low:1.17822,close:1.17831},
{time:1770064620,open:1.17838,high:1.17843,low:1.17837,close:1.17842},
{time:1770064680,open:1.17843,high:1.17868,low:1.17843,close:1.17867},
{time:1770064740,open:1.17865,high:1.17867,low:1.17849,close:1.17851},
{time:1770064800,open:1.17852,high:1.17858,low:1.17851,close:1.17855},
{time:1770064860,open:1.17857,high:1.17857,low:1.17851,close:1.17851},
{time:1770064920,open:1.17851,high:1.17852,low:1.17847,close:1.17851},
{time:1770064980,open:1.17852,high:1.17866,low:1.1785,close:1.17866},
{time:1770065040,open:1.17865,high:1.17865,low:1.17853,close:1.17857},
{time:1770065100,open:1.17856,high:1.17856,low:1.1785,close:1.17854},
{time:1770065160,open:1.17856,high:1.17861,low:1.17855,close:1.17861},
{time:1770065220,open:1.17861,high:1.17883,low:1.17861,close:1.17877},
{time:1770065280,open:1.17875,high:1.17875,low:1.17863,close:1.1787},
{time:1770065340,open:1.17868,high:1.17868,low:1.17857,close:1.17864},
{time:1770065400,open:1.17865,high:1.17886,low:1.17862,close:1.1788},
{time:1770065460,open:1.1788,high:1.17889,low:1.17871,close:1.17883},
{time:1770065520,open:1.17882,high:1.17886,low:1.17881,close:1.17882},
{time:1770065580,open:1.17881,high:1.17881,low:1.17871,close:1.17877},
{time:1770065640,open:1.17878,high:1.17878,low:1.17871,close:1.17875},
{time:1770065700,open:1.17879,high:1.17898,low:1.17879,close:1.17896},
{time:1770065760,open:1.17897,high:1.17904,low:1.17888,close:1.17903},
{time:1770065820,open:1.17904,high:1.17916,low:1.17904,close:1.17912},
{time:1770065880,open:1.17911,high:1.17922,low:1.17905,close:1.17918},
{time:1770065940,open:1.17919,high:1.17919,low:1.17906,close:1.17912},
{time:1770066000,open:1.17914,high:1.17927,low:1.17912,close:1.17923},
{time:1770066060,open:1.17921,high:1.1793,low:1.17913,close:1.17927},
{time:1770066120,open:1.17927,high:1.17927,low:1.17902,close:1.17902},
{time:1770066180,open:1.17902,high:1.17915,low:1.17902,close:1.17913},
{time:1770066240,open:1.17913,high:1.17924,low:1.17913,close:1.17921},
{time:1770066300,open:1.1792,high:1.17922,low:1.17914,close:1.17922},
{time:1770066360,open:1.17921,high:1.17924,low:1.17914,close:1.17917},
{time:1770066420,open:1.17916,high:1.17921,low:1.17916,close:1.17921},
{time:1770066480,open:1.17925,high:1.17931,low:1.17925,close:1.1793},
{time:1770066540,open:1.1793,high:1.1793,low:1.17922,close:1.17922},
{time:1770066600,open:1.17923,high:1.1794,low:1.17923,close:1.17931},
{time:1770066660,open:1.17932,high:1.17937,low:1.1793,close:1.17933},
{time:1770066720,open:1.17933,high:1.17939,low:1.1793,close:1.17933},
{time:1770066780,open:1.17934,high:1.17939,low:1.17931,close:1.17938},
{time:1770066840,open:1.17939,high:1.17942,low:1.17939,close:1.17939},
{time:1770066900,open:1.17943,high:1.17945,low:1.1794,close:1.17941},
{time:1770066960,open:1.17939,high:1.17939,low:1.17936,close:1.17936},
{time:1770067020,open:1.17936,high:1.17938,low:1.17932,close:1.17934},
{time:1770067080,open:1.17932,high:1.17932,low:1.17924,close:1.17924},
{time:1770067140,open:1.17925,high:1.17932,low:1.17925,close:1.17932},
{time:1770067200,open:1.17932,high:1.17932,low:1.17929,close:1.17932},
{time:1770067260,open:1.17933,high:1.1794,low:1.17932,close:1.17937},
{time:1770067320,open:1.17937,high:1.17937,low:1.17931,close:1.17933},
{time:1770067380,open:1.17932,high:1.17933,low:1.17928,close:1.17931},
{time:1770067440,open:1.17932,high:1.17933,low:1.17931,close:1.17932},
{time:1770067500,open:1.17931,high:1.17931,low:1.17929,close:1.1793},
{time:1770067560,open:1.17929,high:1.17929,low:1.17922,close:1.17922},
{time:1770067620,open:1.17921,high:1.17922,low:1.1792,close:1.17922},
{time:1770067680,open:1.17923,high:1.17926,low:1.1792,close:1.17922},
{time:1770067740,open:1.17923,high:1.17928,low:1.17923,close:1.17928},
{time:1770067800,open:1.17927,high:1.17928,low:1.17924,close:1.17924},
{time:1770067860,open:1.17926,high:1.17933,low:1.17926,close:1.17933},
{time:1770067920,open:1.17933,high:1.17937,low:1.17933,close:1.17936},
{time:1770067980,open:1.17936,high:1.17936,low:1.17935,close:1.17936},
{time:1770068040,open:1.17935,high:1.17935,low:1.17919,close:1.17919},
{time:1770068100,open:1.17921,high:1.17921,low:1.1792,close:1.1792},
{time:1770068160,open:1.17919,high:1.17919,low:1.17917,close:1.17917},
{time:1770068220,open:1.17918,high:1.17918,low:1.17917,close:1.17917},
{time:1770068280,open:1.17918,high:1.17921,low:1.17917,close:1.17921},
{time:1770068340,open:1.17921,high:1.17921,low:1.17919,close:1.1792},
{time:1770068400,open:1.17921,high:1.17927,low:1.1792,close:1.17925},
{time:1770068460,open:1.17926,high:1.17927,low:1.17924,close:1.17927},
{time:1770068580,open:1.17927,high:1.17928,low:1.17924,close:1.17926},
{time:1770068640,open:1.17926,high:1.17928,low:1.17926,close:1.17928},
{time:1770068700,open:1.1793,high:1.17935,low:1.1793,close:1.17935},
{time:1770068760,open:1.17934,high:1.17935,low:1.17932,close:1.17935},
{time:1770068820,open:1.17933,high:1.17934,low:1.17933,close:1.17933},
{time:1770068880,open:1.17937,high:1.17937,low:1.17933,close:1.17934},
{time:1770068940,open:1.17935,high:1.17938,low:1.17932,close:1.17932},
{time:1770069000,open:1.17932,high:1.17932,low:1.17905,close:1.17909},
{time:1770069060,open:1.17906,high:1.17914,low:1.17897,close:1.17897},
{time:1770069120,open:1.17895,high:1.17898,low:1.17893,close:1.17897},
{time:1770069180,open:1.17898,high:1.17899,low:1.17897,close:1.17897},
{time:1770069240,open:1.17897,high:1.17899,low:1.17892,close:1.17898},
{time:1770069300,open:1.17897,high:1.17914,low:1.1789,close:1.17913},
{time:1770069360,open:1.17911,high:1.17912,low:1.17889,close:1.17889},
{time:1770069420,open:1.17888,high:1.17893,low:1.17883,close:1.17889},
{time:1770069480,open:1.17887,high:1.17904,low:1.17886,close:1.17904},
{time:1770069540,open:1.17903,high:1.17907,low:1.17897,close:1.17904},
{time:1770069600,open:1.17903,high:1.17903,low:1.17882,close:1.17882},
{time:1770069660,open:1.17883,high:1.17883,low:1.17868,close:1.1787},
{time:1770069840,open:1.1787,high:1.1787,low:1.1785,close:1.1785},
{time:1770069900,open:1.1785,high:1.17879,low:1.1785,close:1.17879},
{time:1770069960,open:1.17879,high:1.17887,low:1.17879,close:1.17883},
{time:1770070020,open:1.17894,high:1.17895,low:1.17884,close:1.17885},
{time:1770070080,open:1.17895,high:1.17896,low:1.17878,close:1.17883},
{time:1770070140,open:1.17888,high:1.17888,low:1.17873,close:1.17888},
{time:1770070200,open:1.17889,high:1.17892,low:1.17875,close:1.17892},
{time:1770070260,open:1.17901,high:1.17909,low:1.17896,close:1.17896},
{time:1770070320,open:1.17907,high:1.17907,low:1.17888,close:1.17888},
{time:1770070380,open:1.1787,high:1.17876,low:1.1787,close:1.17875},
{time:1770070440,open:1.17875,high:1.17889,low:1.17872,close:1.17889},
{time:1770070500,open:1.17889,high:1.17891,low:1.17888,close:1.17888},
{time:1770070620,open:1.17891,high:1.17891,low:1.1789,close:1.1789},
{time:1770070680,open:1.1789,high:1.1789,low:1.17889,close:1.1789},
{time:1770070740,open:1.1789,high:1.17891,low:1.17888,close:1.17891},
{time:1770070800,open:1.17892,high:1.17895,low:1.17874,close:1.17895},
{time:1770070860,open:1.17895,high:1.17903,low:1.17893,close:1.17903},
{time:1770070920,open:1.17904,high:1.1791,low:1.17902,close:1.17909},
{time:1770070980,open:1.17913,high:1.17914,low:1.17902,close:1.17912},
{time:1770071040,open:1.17914,high:1.17918,low:1.17914,close:1.17918},
{time:1770071100,open:1.17921,high:1.17921,low:1.1789,close:1.17898},
{time:1770071160,open:1.17898,high:1.17898,low:1.17878,close:1.17878},
{time:1770071220,open:1.1788,high:1.17887,low:1.17878,close:1.17886},
{time:1770071280,open:1.17885,high:1.17889,low:1.1788,close:1.17888},
{time:1770071340,open:1.17881,high:1.17888,low:1.17878,close:1.17882},
{time:1770071400,open:1.17885,high:1.17898,low:1.17885,close:1.17897},
{time:1770071460,open:1.17899,high:1.17901,low:1.17898,close:1.17901},
{time:1770071520,open:1.17899,high:1.179,low:1.17892,close:1.17892},
{time:1770071580,open:1.17893,high:1.17894,low:1.17892,close:1.17894},
{time:1770071640,open:1.17894,high:1.17896,low:1.17893,close:1.17893},
{time:1770071700,open:1.17895,high:1.17898,low:1.17893,close:1.17896},
{time:1770071760,open:1.17895,high:1.179,low:1.17892,close:1.17895},
{time:1770071820,open:1.17893,high:1.179,low:1.17891,close:1.17896},
{time:1770071880,open:1.17897,high:1.17899,low:1.17891,close:1.17893},
{time:1770071940,open:1.17891,high:1.17896,low:1.17891,close:1.17893},
{time:1770072000,open:1.17893,high:1.17901,low:1.17891,close:1.17899},
{time:1770072060,open:1.17901,high:1.17904,low:1.17892,close:1.179},
{time:1770072120,open:1.179,high:1.17903,low:1.17892,close:1.17898},
{time:1770072180,open:1.17898,high:1.17901,low:1.17892,close:1.17892},
{time:1770072240,open:1.17891,high:1.17895,low:1.17891,close:1.17891},
{time:1770072300,open:1.17894,high:1.17901,low:1.17891,close:1.17891},
{time:1770072360,open:1.17892,high:1.179,low:1.17891,close:1.17891},
{time:1770072420,open:1.179,high:1.17902,low:1.17891,close:1.17898},
{time:1770072480,open:1.17901,high:1.17904,low:1.17891,close:1.17899},
{time:1770072540,open:1.17897,high:1.17904,low:1.17891,close:1.17891},
{time:1770072600,open:1.17899,high:1.179,low:1.17891,close:1.17892},
{time:1770072660,open:1.17916,high:1.17917,low:1.17899,close:1.17905},
{time:1770072720,open:1.17905,high:1.17917,low:1.17889,close:1.17895},
{time:1770072780,open:1.17894,high:1.17902,low:1.17888,close:1.17893},
{time:1770072840,open:1.17899,high:1.17902,low:1.17892,close:1.17898},
{time:1770072900,open:1.17898,high:1.17906,low:1.17896,close:1.17906},
{time:1770072960,open:1.17906,high:1.17907,low:1.17895,close:1.17898},
{time:1770073020,open:1.17898,high:1.17901,low:1.17895,close:1.17895},
{time:1770073080,open:1.17895,high:1.17896,low:1.17894,close:1.17895},
{time:1770073140,open:1.17895,high:1.17898,low:1.17882,close:1.17882},
{time:1770073200,open:1.17883,high:1.17905,low:1.1787,close:1.1787},
{time:1770073260,open:1.17877,high:1.17879,low:1.17869,close:1.17875},
{time:1770073320,open:1.17877,high:1.17889,low:1.17877,close:1.17889},
{time:1770073380,open:1.17889,high:1.17924,low:1.17889,close:1.1792},
{time:1770073440,open:1.17915,high:1.17922,low:1.17914,close:1.17917},
{time:1770073500,open:1.17913,high:1.17929,low:1.17907,close:1.17926},
{time:1770073560,open:1.17923,high:1.17929,low:1.17923,close:1.17929},
{time:1770073620,open:1.17928,high:1.17931,low:1.17925,close:1.17931},
{time:1770073680,open:1.17932,high:1.17932,low:1.17927,close:1.17929},
{time:1770073740,open:1.17931,high:1.17933,low:1.17922,close:1.17923},
{time:1770073800,open:1.17923,high:1.17937,low:1.1792,close:1.17936},
{time:1770073860,open:1.17936,high:1.17936,low:1.17927,close:1.17929},
{time:1770073920,open:1.17935,high:1.17937,low:1.1793,close:1.17936},
{time:1770073980,open:1.17935,high:1.17942,low:1.17932,close:1.17942},
{time:1770074040,open:1.1794,high:1.1794,low:1.17932,close:1.17934},
{time:1770074100,open:1.17934,high:1.17941,low:1.17934,close:1.17938},
{time:1770074160,open:1.17943,high:1.17943,low:1.1794,close:1.17941},
{time:1770074220,open:1.17941,high:1.17947,low:1.17941,close:1.17945},
{time:1770074280,open:1.17944,high:1.17952,low:1.17944,close:1.1795},
{time:1770074340,open:1.17953,high:1.17966,low:1.17953,close:1.17957},
{time:1770074400,open:1.17956,high:1.17956,low:1.17949,close:1.17952},
{time:1770074460,open:1.17952,high:1.17969,low:1.17952,close:1.17968},
{time:1770074520,open:1.17966,high:1.17967,low:1.17963,close:1.17966},
{time:1770074580,open:1.17972,high:1.17978,low:1.17972,close:1.17978},
{time:1770074640,open:1.17977,high:1.17981,low:1.17977,close:1.17979},
{time:1770074700,open:1.17977,high:1.17977,low:1.17972,close:1.17973},
{time:1770074760,open:1.17972,high:1.17974,low:1.17966,close:1.17967},
{time:1770074820,open:1.17968,high:1.17969,low:1.17962,close:1.17966},
{time:1770074880,open:1.17967,high:1.17967,low:1.17964,close:1.17965},
{time:1770074940,open:1.17962,high:1.17962,low:1.17958,close:1.17958},
{time:1770075000,open:1.17957,high:1.17957,low:1.1795,close:1.17952},
{time:1770075060,open:1.17954,high:1.17958,low:1.17949,close:1.17949},
{time:1770075120,open:1.1795,high:1.17968,low:1.1795,close:1.17967},
{time:1770075180,open:1.17966,high:1.17981,low:1.17962,close:1.17969},
{time:1770075240,open:1.1797,high:1.17974,low:1.1797,close:1.17972},
{time:1770075300,open:1.17973,high:1.17974,low:1.17969,close:1.17969},
{time:1770075360,open:1.17969,high:1.1797,low:1.17962,close:1.17964},
{time:1770075420,open:1.17964,high:1.17968,low:1.17963,close:1.17968},
{time:1770075480,open:1.17966,high:1.17967,low:1.17963,close:1.17965},
{time:1770075540,open:1.17968,high:1.17978,low:1.17968,close:1.17976},
{time:1770075600,open:1.17974,high:1.17977,low:1.17967,close:1.1797},
{time:1770075660,open:1.17972,high:1.17972,low:1.17965,close:1.17965},
{time:1770075720,open:1.17964,high:1.17966,low:1.17953,close:1.17953},
{time:1770075780,open:1.17955,high:1.17964,low:1.17955,close:1.17962},
{time:1770075840,open:1.17963,high:1.17963,low:1.17959,close:1.1796},
{time:1770075900,open:1.17961,high:1.17963,low:1.17951,close:1.17953},
{time:1770075960,open:1.17953,high:1.17954,low:1.17944,close:1.17947},
{time:1770076020,open:1.17945,high:1.17946,low:1.17945,close:1.17945},
{time:1770076080,open:1.17947,high:1.17947,low:1.17939,close:1.17944},
{time:1770076140,open:1.17944,high:1.17958,low:1.17944,close:1.17958},
{time:1770076200,open:1.17959,high:1.17966,low:1.17959,close:1.17965},
{time:1770076260,open:1.17966,high:1.17979,low:1.17966,close:1.17978},
{time:1770076320,open:1.17978,high:1.17978,low:1.17969,close:1.1797},
{time:1770076380,open:1.17968,high:1.17969,low:1.17963,close:1.17963},
{time:1770076440,open:1.1796,high:1.1796,low:1.17953,close:1.17958},
{time:1770076500,open:1.17958,high:1.17966,low:1.17954,close:1.17965},
{time:1770076560,open:1.17966,high:1.17977,low:1.17965,close:1.17977},
{time:1770076620,open:1.17977,high:1.17989,low:1.17977,close:1.17986},
{time:1770076680,open:1.17985,high:1.17987,low:1.17978,close:1.17982},
{time:1770076740,open:1.17985,high:1.17989,low:1.17983,close:1.17987},
{time:1770076800,open:1.17986,high:1.17992,low:1.1798,close:1.17991},
{time:1770076860,open:1.17992,high:1.17993,low:1.17979,close:1.17988},
{time:1770076920,open:1.17991,high:1.18015,low:1.17991,close:1.18015},
{time:1770076980,open:1.18014,high:1.18018,low:1.18012,close:1.18013},
{time:1770077040,open:1.18011,high:1.18011,low:1.17996,close:1.18},
{time:1770077100,open:1.18001,high:1.18003,low:1.17987,close:1.18002},
{time:1770077160,open:1.18004,high:1.1801,low:1.18002,close:1.18007},
{time:1770077220,open:1.18007,high:1.18021,low:1.18003,close:1.18021},
{time:1770077280,open:1.18022,high:1.18042,low:1.18022,close:1.18038},
{time:1770077340,open:1.18037,high:1.18037,low:1.18025,close:1.18029},
{time:1770077400,open:1.18028,high:1.18029,low:1.18012,close:1.18028},
{time:1770077460,open:1.18028,high:1.18032,low:1.18026,close:1.18029},
{time:1770077520,open:1.18028,high:1.18029,low:1.18015,close:1.18018},
{time:1770077580,open:1.18016,high:1.18025,low:1.18016,close:1.1802},
{time:1770077640,open:1.18022,high:1.18034,low:1.1802,close:1.18032},
{time:1770077700,open:1.18034,high:1.18044,low:1.18027,close:1.18041},
{time:1770077760,open:1.18042,high:1.18042,low:1.18021,close:1.18036},
{time:1770077820,open:1.18036,high:1.18043,low:1.18029,close:1.18043},
{time:1770077880,open:1.18041,high:1.18054,low:1.18039,close:1.18042},
{time:1770077940,open:1.18043,high:1.18049,low:1.18036,close:1.18047},
{time:1770078000,open:1.18043,high:1.18053,low:1.18038,close:1.1804},
{time:1770078060,open:1.1804,high:1.18047,low:1.1804,close:1.18046},
{time:1770078120,open:1.18044,high:1.18047,low:1.18037,close:1.18039},
{time:1770078180,open:1.18039,high:1.18047,low:1.18039,close:1.18044},
{time:1770078240,open:1.18046,high:1.1805,low:1.18043,close:1.18045},
{time:1770078300,open:1.18047,high:1.18048,low:1.18043,close:1.18043},
{time:1770078360,open:1.18042,high:1.18055,low:1.18038,close:1.18052},
{time:1770078420,open:1.18053,high:1.18054,low:1.18044,close:1.18053},
{time:1770078480,open:1.18053,high:1.18057,low:1.18046,close:1.18048},
{time:1770078540,open:1.18048,high:1.18049,low:1.18042,close:1.18048},
{time:1770078600,open:1.18048,high:1.18053,low:1.18045,close:1.18053},
{time:1770078660,open:1.18054,high:1.1806,low:1.18051,close:1.18056},
{time:1770078720,open:1.18059,high:1.18062,low:1.18055,close:1.18055},
{time:1770078780,open:1.18055,high:1.18055,low:1.1805,close:1.18055},
{time:1770078840,open:1.18055,high:1.18062,low:1.18052,close:1.18053},
{time:1770078900,open:1.18054,high:1.18055,low:1.18049,close:1.18053},
{time:1770078960,open:1.18052,high:1.18069,low:1.18052,close:1.18069},
{time:1770079020,open:1.18069,high:1.18069,low:1.18059,close:1.18062},
{time:1770079080,open:1.1806,high:1.1806,low:1.18054,close:1.18059},
{time:1770079140,open:1.18057,high:1.18057,low:1.18045,close:1.18046},
{time:1770079200,open:1.18046,high:1.1805,low:1.18045,close:1.18047},
{time:1770079260,open:1.18047,high:1.18049,low:1.18047,close:1.18049},
{time:1770079320,open:1.1805,high:1.18053,low:1.1804,close:1.1804},
{time:1770079380,open:1.18041,high:1.18041,low:1.18029,close:1.1803},
{time:1770079440,open:1.18029,high:1.18035,low:1.18029,close:1.18029},
{time:1770079500,open:1.18029,high:1.18029,low:1.18015,close:1.18021},
{time:1770079560,open:1.18021,high:1.18035,low:1.1802,close:1.18035},
{time:1770079620,open:1.18034,high:1.18038,low:1.18029,close:1.18033},
{time:1770079680,open:1.18031,high:1.18034,low:1.1803,close:1.18034},
{time:1770079740,open:1.18035,high:1.18036,low:1.18033,close:1.18035},
{time:1770079800,open:1.18034,high:1.18037,low:1.18027,close:1.18027},
{time:1770079860,open:1.18029,high:1.18029,low:1.18022,close:1.18023},
{time:1770079920,open:1.18024,high:1.18034,low:1.18022,close:1.18034},
{time:1770079980,open:1.18035,high:1.18051,low:1.18035,close:1.18045},
{time:1770080040,open:1.18044,high:1.18046,low:1.18034,close:1.18036},
{time:1770080100,open:1.18035,high:1.18036,low:1.18019,close:1.1803},
{time:1770080160,open:1.18031,high:1.18043,low:1.18025,close:1.18042},
{time:1770080220,open:1.1804,high:1.18063,low:1.18039,close:1.18061},
{time:1770080280,open:1.1806,high:1.18063,low:1.18058,close:1.18061},
{time:1770080340,open:1.18062,high:1.18064,low:1.18057,close:1.18063},
{time:1770080400,open:1.18064,high:1.18067,low:1.1805,close:1.18053},
{time:1770080460,open:1.1805,high:1.1806,low:1.18043,close:1.18043},
{time:1770080520,open:1.18043,high:1.18059,low:1.18034,close:1.18058},
{time:1770080580,open:1.18057,high:1.18057,low:1.18033,close:1.18033},
{time:1770080640,open:1.18033,high:1.18048,low:1.18024,close:1.18048},
{time:1770080700,open:1.18044,high:1.18045,low:1.18024,close:1.18026},
{time:1770080760,open:1.18026,high:1.18034,low:1.18026,close:1.18029},
{time:1770080820,open:1.18028,high:1.18028,low:1.18015,close:1.18019},
{time:1770080880,open:1.18019,high:1.18033,low:1.18019,close:1.1803},
{time:1770080940,open:1.18031,high:1.18033,low:1.18028,close:1.18033},
{time:1770081000,open:1.18031,high:1.18053,low:1.18031,close:1.18053},
{time:1770081060,open:1.18054,high:1.18079,low:1.18054,close:1.18059},
{time:1770081120,open:1.18062,high:1.18073,low:1.18053,close:1.18073},
{time:1770081180,open:1.18072,high:1.18076,low:1.18067,close:1.18069},
{time:1770081240,open:1.18069,high:1.18077,low:1.18065,close:1.18076},
{time:1770081300,open:1.18075,high:1.18104,low:1.18075,close:1.181},
{time:1770081360,open:1.18102,high:1.18103,low:1.18094,close:1.18097},
{time:1770081420,open:1.18098,high:1.18098,low:1.18082,close:1.18082},
{time:1770081480,open:1.18084,high:1.18089,low:1.18084,close:1.18086},
{time:1770081540,open:1.18086,high:1.18099,low:1.18076,close:1.18099},
{time:1770081600,open:1.18101,high:1.18109,low:1.18101,close:1.18107},
{time:1770081660,open:1.18106,high:1.18108,low:1.18089,close:1.18091},
{time:1770081720,open:1.18091,high:1.18097,low:1.18086,close:1.18096},
{time:1770081780,open:1.18096,high:1.18096,low:1.18073,close:1.18093},
{time:1770081840,open:1.18093,high:1.18099,low:1.18092,close:1.18098},
{time:1770081900,open:1.18099,high:1.18103,low:1.18098,close:1.181},
{time:1770081960,open:1.18101,high:1.18101,low:1.18094,close:1.18098},
{time:1770082020,open:1.18098,high:1.18101,low:1.18094,close:1.18101},
{time:1770082080,open:1.18104,high:1.18106,low:1.18097,close:1.18099},
{time:1770082140,open:1.181,high:1.18103,low:1.1808,close:1.18083},
{time:1770082200,open:1.18081,high:1.18097,low:1.1808,close:1.18094},
{time:1770082260,open:1.18094,high:1.18101,low:1.18091,close:1.18095},
{time:1770082320,open:1.18096,high:1.18106,low:1.18095,close:1.18101},
{time:1770082380,open:1.18101,high:1.18101,low:1.18074,close:1.1808},
{time:1770082440,open:1.1808,high:1.181,low:1.1808,close:1.18098},
{time:1770082500,open:1.18099,high:1.18126,low:1.18097,close:1.18125},
{time:1770082560,open:1.18124,high:1.18128,low:1.18122,close:1.18122},
{time:1770082620,open:1.18123,high:1.18125,low:1.1812,close:1.18124},
{time:1770082680,open:1.18123,high:1.18123,low:1.18107,close:1.18115},
{time:1770082740,open:1.18116,high:1.18117,low:1.18109,close:1.18112},
{time:1770082800,open:1.18112,high:1.18118,low:1.18109,close:1.18117},
{time:1770082860,open:1.18118,high:1.1812,low:1.18114,close:1.18119},
{time:1770082920,open:1.18122,high:1.18127,low:1.18114,close:1.18127},
{time:1770082980,open:1.18127,high:1.18128,low:1.18121,close:1.18126},
{time:1770083040,open:1.18128,high:1.1813,low:1.18124,close:1.18124},
{time:1770083100,open:1.18124,high:1.18124,low:1.18104,close:1.18108},
{time:1770083160,open:1.18107,high:1.18117,low:1.18105,close:1.18108},
{time:1770083220,open:1.18106,high:1.18116,low:1.18103,close:1.18112},
{time:1770083280,open:1.18112,high:1.18112,low:1.18104,close:1.18109},
{time:1770083340,open:1.18108,high:1.18109,low:1.18101,close:1.18101},
{time:1770083400,open:1.18102,high:1.18104,low:1.18097,close:1.18099},
{time:1770083460,open:1.18099,high:1.18109,low:1.18098,close:1.18101},
{time:1770083520,open:1.18101,high:1.18113,low:1.18098,close:1.18113},
{time:1770083580,open:1.18112,high:1.18121,low:1.1811,close:1.18118},
{time:1770083640,open:1.1812,high:1.18122,low:1.18112,close:1.18113},
{time:1770083700,open:1.18112,high:1.18129,low:1.18111,close:1.18129},
{time:1770083760,open:1.18132,high:1.18132,low:1.18129,close:1.18131},
{time:1770083820,open:1.1813,high:1.18138,low:1.18128,close:1.18137},
{time:1770083880,open:1.18137,high:1.18149,low:1.18137,close:1.18146},
{time:1770083940,open:1.18145,high:1.18146,low:1.18132,close:1.18139},
{time:1770084000,open:1.18139,high:1.18139,low:1.1813,close:1.18133},
{time:1770084060,open:1.18132,high:1.18143,low:1.18129,close:1.18138},
{time:1770084120,open:1.18139,high:1.18141,low:1.18124,close:1.18125},
{time:1770084180,open:1.18126,high:1.18127,low:1.18113,close:1.18114},
{time:1770084240,open:1.18114,high:1.1812,low:1.18103,close:1.18109},
{time:1770084300,open:1.18107,high:1.18109,low:1.18098,close:1.18102},
{time:1770084360,open:1.18103,high:1.18105,low:1.18097,close:1.18102},
{time:1770084420,open:1.18099,high:1.181,low:1.18097,close:1.18099},
{time:1770084480,open:1.18099,high:1.18104,low:1.18098,close:1.18103},
{time:1770084540,open:1.18103,high:1.18113,low:1.18102,close:1.18112},
{time:1770084600,open:1.18113,high:1.18114,low:1.18104,close:1.18107},
{time:1770084660,open:1.18106,high:1.18108,low:1.18098,close:1.181},
{time:1770084720,open:1.181,high:1.18117,low:1.18097,close:1.1811},
{time:1770084780,open:1.1811,high:1.1811,low:1.1809,close:1.18092},
{time:1770084840,open:1.18092,high:1.18103,low:1.18092,close:1.18103},
{time:1770084900,open:1.18101,high:1.18101,low:1.18075,close:1.18078},
{time:1770084960,open:1.18079,high:1.18093,low:1.18079,close:1.18093},
{time:1770085020,open:1.18092,high:1.18094,low:1.18089,close:1.18092},
{time:1770085080,open:1.18092,high:1.18094,low:1.18087,close:1.18092},
{time:1770085140,open:1.18092,high:1.18092,low:1.18081,close:1.18084},
{time:1770085200,open:1.18083,high:1.18089,low:1.18076,close:1.18076},
{time:1770085260,open:1.18077,high:1.18093,low:1.18077,close:1.18083},
{time:1770085320,open:1.18081,high:1.18086,low:1.1808,close:1.18086},
{time:1770085380,open:1.18085,high:1.1809,low:1.18079,close:1.1809},
{time:1770085440,open:1.18089,high:1.18095,low:1.18089,close:1.18094},
{time:1770085500,open:1.18092,high:1.18094,low:1.18076,close:1.1808},
{time:1770085560,open:1.1808,high:1.1808,low:1.18072,close:1.18075},
{time:1770085620,open:1.18073,high:1.18075,low:1.18069,close:1.18073},
{time:1770085680,open:1.18072,high:1.18073,low:1.18067,close:1.18071},
{time:1770085740,open:1.18073,high:1.18073,low:1.18063,close:1.18063},
{time:1770085800,open:1.18063,high:1.18066,low:1.18059,close:1.18064},
{time:1770085860,open:1.18067,high:1.18073,low:1.18063,close:1.18073},
{time:1770085920,open:1.18072,high:1.18072,low:1.18064,close:1.18071},
{time:1770085980,open:1.18071,high:1.18073,low:1.18068,close:1.18069},
{time:1770086040,open:1.1807,high:1.18071,low:1.18064,close:1.18064},
{time:1770086100,open:1.18063,high:1.18069,low:1.18063,close:1.18065},
{time:1770086160,open:1.18065,high:1.18065,low:1.18058,close:1.18064},
{time:1770086220,open:1.18066,high:1.18068,low:1.18056,close:1.1806},
{time:1770086280,open:1.1806,high:1.18063,low:1.18059,close:1.1806},
{time:1770086340,open:1.18062,high:1.18069,low:1.1806,close:1.18064},
{time:1770086400,open:1.18063,high:1.18063,low:1.18057,close:1.18058},
{time:1770086460,open:1.18054,high:1.18057,low:1.18043,close:1.18049},
{time:1770086520,open:1.1805,high:1.18051,low:1.18046,close:1.18048},
{time:1770086580,open:1.18046,high:1.18046,low:1.18027,close:1.18036},
{time:1770086640,open:1.18035,high:1.18035,low:1.18026,close:1.18028},
{time:1770086700,open:1.18029,high:1.18033,low:1.18027,close:1.18029},
{time:1770086760,open:1.18028,high:1.1803,low:1.18025,close:1.18027},
{time:1770086820,open:1.18028,high:1.18028,low:1.18022,close:1.18028},
{time:1770086880,open:1.18026,high:1.18028,low:1.18017,close:1.18017},
{time:1770086940,open:1.18016,high:1.18023,low:1.18016,close:1.18019},
{time:1770087000,open:1.18019,high:1.18021,low:1.18007,close:1.18012},
{time:1770087060,open:1.18014,high:1.18018,low:1.18007,close:1.18015},
{time:1770087120,open:1.18015,high:1.18019,low:1.1801,close:1.18013},
{time:1770087180,open:1.18012,high:1.1802,low:1.18012,close:1.18017},
{time:1770087240,open:1.18019,high:1.18032,low:1.18018,close:1.18031},
{time:1770087300,open:1.18033,high:1.18033,low:1.18015,close:1.18015},
{time:1770087360,open:1.18016,high:1.18019,low:1.18015,close:1.18017},
{time:1770087420,open:1.18018,high:1.1802,low:1.18013,close:1.18015},
{time:1770087480,open:1.18018,high:1.18019,low:1.18008,close:1.18012},
{time:1770087540,open:1.18012,high:1.18017,low:1.18008,close:1.18013},
{time:1770087600,open:1.18011,high:1.18015,low:1.18008,close:1.18015},
{time:1770087660,open:1.18014,high:1.18017,low:1.18009,close:1.18013},
{time:1770087720,open:1.18013,high:1.18017,low:1.18012,close:1.18017},
{time:1770087780,open:1.18014,high:1.18019,low:1.18011,close:1.18012},
{time:1770087840,open:1.18014,high:1.18017,low:1.18013,close:1.18015},
{time:1770087900,open:1.18014,high:1.18026,low:1.18014,close:1.18022},
{time:1770087960,open:1.18024,high:1.1803,low:1.18024,close:1.18028},
{time:1770088020,open:1.18026,high:1.18029,low:1.18022,close:1.18022},
{time:1770088080,open:1.18021,high:1.18027,low:1.18016,close:1.18023},
{time:1770088140,open:1.1803,high:1.18033,low:1.18026,close:1.18031},
{time:1770088200,open:1.18033,high:1.18033,low:1.18026,close:1.18026},
{time:1770088260,open:1.18026,high:1.18027,low:1.18018,close:1.18023},
{time:1770088320,open:1.18021,high:1.18023,low:1.18021,close:1.18023},
{time:1770088380,open:1.1802,high:1.1802,low:1.18014,close:1.18018},
{time:1770088440,open:1.18019,high:1.18021,low:1.18016,close:1.18019},
{time:1770088500,open:1.18019,high:1.18035,low:1.18019,close:1.18035},
{time:1770088560,open:1.18035,high:1.18035,low:1.18022,close:1.18026},
{time:1770088620,open:1.18027,high:1.18033,low:1.18027,close:1.18033},
{time:1770088680,open:1.18032,high:1.18038,low:1.18029,close:1.18038},
{time:1770088740,open:1.18037,high:1.18043,low:1.18023,close:1.18024},
{time:1770088800,open:1.18025,high:1.1803,low:1.1802,close:1.18026},
{time:1770088860,open:1.18027,high:1.18038,low:1.18025,close:1.18037},
{time:1770088920,open:1.18037,high:1.18042,low:1.1803,close:1.18031},
{time:1770088980,open:1.18029,high:1.18032,low:1.18025,close:1.18027},
{time:1770089040,open:1.18029,high:1.18029,low:1.18024,close:1.18028},
{time:1770089100,open:1.18027,high:1.18027,low:1.18023,close:1.18025},
{time:1770089160,open:1.18024,high:1.18024,low:1.18023,close:1.18023},
{time:1770089220,open:1.18024,high:1.18031,low:1.18024,close:1.18031},
{time:1770089280,open:1.18028,high:1.18041,low:1.18028,close:1.18038},
{time:1770089340,open:1.18038,high:1.18047,low:1.18038,close:1.1804},
{time:1770089400,open:1.18039,high:1.18068,low:1.18036,close:1.18068},
{time:1770089460,open:1.18069,high:1.18071,low:1.18059,close:1.18068},
{time:1770089520,open:1.18068,high:1.18073,low:1.18048,close:1.18049},
{time:1770089580,open:1.18047,high:1.18061,low:1.18047,close:1.18052},
{time:1770089640,open:1.1805,high:1.18074,low:1.1805,close:1.18069},
{time:1770089700,open:1.18067,high:1.18073,low:1.18057,close:1.18067},
{time:1770089760,open:1.18068,high:1.18077,low:1.18067,close:1.18074},
{time:1770089820,open:1.18073,high:1.18083,low:1.18071,close:1.18077},
{time:1770089880,open:1.18076,high:1.18096,low:1.18075,close:1.18092},
{time:1770089940,open:1.18093,high:1.18105,low:1.18082,close:1.18096},
{time:1770090000,open:1.18096,high:1.18105,low:1.18092,close:1.18095},
{time:1770090060,open:1.18095,high:1.18106,low:1.1809,close:1.18102},
{time:1770090120,open:1.18104,high:1.18118,low:1.18103,close:1.18113},
{time:1770090180,open:1.18114,high:1.18114,low:1.18101,close:1.18106},
{time:1770090240,open:1.18107,high:1.18112,low:1.18103,close:1.18107},
{time:1770090300,open:1.18108,high:1.18113,low:1.18099,close:1.181},
{time:1770090360,open:1.18102,high:1.18118,low:1.18101,close:1.18112},
{time:1770090420,open:1.18111,high:1.18111,low:1.18101,close:1.18104},
{time:1770090480,open:1.18103,high:1.18106,low:1.18099,close:1.18104},
{time:1770090540,open:1.18105,high:1.18105,low:1.18091,close:1.18094},
{time:1770090600,open:1.18095,high:1.18108,low:1.18094,close:1.18106},
{time:1770090660,open:1.18105,high:1.18113,low:1.18097,close:1.18098},
{time:1770090720,open:1.18099,high:1.18103,low:1.18097,close:1.18097},
{time:1770090780,open:1.18098,high:1.18111,low:1.18098,close:1.18109},
{time:1770090840,open:1.18111,high:1.18118,low:1.18111,close:1.18117},
{time:1770090900,open:1.18119,high:1.18121,low:1.18115,close:1.18116},
{time:1770090960,open:1.18115,high:1.18115,low:1.18093,close:1.18093},
{time:1770091020,open:1.18093,high:1.18119,low:1.18093,close:1.18119},
{time:1770091080,open:1.18123,high:1.18126,low:1.18117,close:1.18124},
{time:1770091140,open:1.18121,high:1.18121,low:1.18093,close:1.18093},
{time:1770091200,open:1.18092,high:1.18113,low:1.18092,close:1.18108},
{time:1770091260,open:1.18107,high:1.18108,low:1.18103,close:1.18105},
{time:1770091320,open:1.18103,high:1.18129,low:1.18103,close:1.18129},
{time:1770091380,open:1.18128,high:1.18136,low:1.18127,close:1.18129},
{time:1770091440,open:1.18128,high:1.18133,low:1.18111,close:1.18114},
{time:1770091500,open:1.18116,high:1.18135,low:1.18114,close:1.18132},
{time:1770091560,open:1.18131,high:1.18132,low:1.18123,close:1.18132},
{time:1770091620,open:1.18132,high:1.18133,low:1.18129,close:1.18132},
{time:1770091680,open:1.18133,high:1.18138,low:1.18132,close:1.18138},
{time:1770091740,open:1.18136,high:1.18147,low:1.18136,close:1.18146},
{time:1770091800,open:1.18147,high:1.1815,low:1.18145,close:1.18145},
{time:1770091860,open:1.18141,high:1.18146,low:1.18135,close:1.18135},
{time:1770091920,open:1.18134,high:1.18142,low:1.18131,close:1.18139},
{time:1770091980,open:1.18135,high:1.18138,low:1.18128,close:1.18135},
{time:1770092040,open:1.18135,high:1.18141,low:1.1813,close:1.18132},
{time:1770092100,open:1.18131,high:1.18135,low:1.1813,close:1.18132},
{time:1770092160,open:1.18131,high:1.18136,low:1.1813,close:1.18134},
{time:1770092220,open:1.18136,high:1.1814,low:1.18129,close:1.18139},
{time:1770092280,open:1.18138,high:1.18144,low:1.18138,close:1.1814},
{time:1770092340,open:1.18141,high:1.18148,low:1.18141,close:1.18143},
{time:1770092400,open:1.18143,high:1.18162,low:1.18143,close:1.18161},
{time:1770092460,open:1.18161,high:1.18161,low:1.18154,close:1.1816},
{time:1770092520,open:1.18158,high:1.18165,low:1.18154,close:1.18161},
{time:1770092580,open:1.18162,high:1.18164,low:1.18159,close:1.18161}
];
  var ENTRY_IDX=1616, EXIT_IDX=1806;
  var ENTRY_PRICE=1.18031, EXIT_PRICE=1.18161;
  var TP_PRICE=1.18160, SL_PRICE=1.17950;
  var PIPS=Math.round((EXIT_PRICE-ENTRY_PRICE)*10000);
  var TICK=50;

  var el=document.getElementById('chart');
  var W=el.offsetWidth||window.innerWidth;
  var H=Math.max(el.offsetHeight||0,300);

  var chart=LightweightCharts.createChart(el,{
    width:W,height:H,
    layout:{background:{color:'#131722'},textColor:'#787b86'},
    grid:{vertLines:{color:'rgba(255,255,255,0.04)'},horzLines:{color:'rgba(255,255,255,0.04)'}},
    timeScale:{timeVisible:true,secondsVisible:false,rightOffset:6,barSpacing:14,borderColor:'#2a2e39'},
    rightPriceScale:{borderColor:'#2a2e39',scaleMargins:{top:0.10,bottom:0.14}},
    crosshair:{
      vertLine:{color:'rgba(255,255,255,0.18)',labelBackgroundColor:'#2a2e39'},
      horzLine:{color:'rgba(255,255,255,0.18)',labelBackgroundColor:'#2a2e39'}
    }
  });

  new ResizeObserver(function(){chart.applyOptions({width:el.offsetWidth,height:el.offsetHeight})}).observe(el);

  var cs=chart.addCandlestickSeries({
    upColor:'#26a69a',downColor:'#ef5350',
    wickUpColor:'#26a69a',wickDownColor:'#ef5350',borderVisible:false
  });

  var LS=LightweightCharts.LineStyle;
  var slS=chart.addLineSeries({color:'rgba(239,83,80,.6)',lineWidth:1,lineStyle:LS?LS.Dashed:1,
    lastValueVisible:false,priceLineVisible:false,crosshairMarkerVisible:false});
  var tpS=chart.addLineSeries({color:'rgba(38,166,154,.6)',lineWidth:1,lineStyle:LS?LS.Dashed:1,
    lastValueVisible:false,priceLineVisible:false,crosshairMarkerVisible:false});

  var rb=document.getElementById('rbadge');
  var tp=document.getElementById('tpanel');
  var f5=function(v){return v.toFixed(5)};
  var idx=ENTRY_IDX;
  var animDone=false;

  function tick(){
    if(idx>=CANDLES.length){animDone=true;return}
    cs.update(CANDLES[idx]);
    document.getElementById('bc').textContent=String(idx+1);
    if(idx>=ENTRY_IDX){
      var t0=CANDLES[ENTRY_IDX].time,t1=CANDLES[idx].time;
      slS.setData([{time:t0,value:SL_PRICE},{time:t1,value:SL_PRICE}]);
      tpS.setData([{time:t0,value:TP_PRICE},{time:t1,value:TP_PRICE}]);
      document.getElementById('tpentry').textContent=f5(ENTRY_PRICE);
      rb.classList.add('on');
    }
    var mk=[];
    if(idx>=ENTRY_IDX) mk.push({time:CANDLES[ENTRY_IDX].time,position:'belowBar',color:'#26a69a',shape:'arrowUp',text:'LONG'});
    if(idx>=EXIT_IDX){
      mk.push({time:CANDLES[EXIT_IDX].time,position:'aboveBar',color:'#26a69a',shape:'circle',text:'\u2713'});
      document.getElementById('tpexit').textContent=f5(EXIT_PRICE);
      document.getElementById('tppips').textContent='+'+PIPS;
      document.getElementById('tppnl').textContent='+$'+(PIPS*0.99).toFixed(2);
      tp.classList.add('on');
      document.getElementById('tc').textContent='1';
      document.getElementById('wr').textContent='100%';
      document.getElementById('pnl').textContent='+$'+(PIPS*0.99).toFixed(2);
    }
    cs.setMarkers(mk);
    idx++;
    if(idx<=CANDLES.length) setTimeout(tick,TICK);
    else animDone=true;
  }

  // Afficher toutes les bougies contexte d'un coup, fixer la vue sur tout le range, puis animer
  cs.setData(CANDLES.slice(0,ENTRY_IDX));
  chart.timeScale().setVisibleLogicalRange({from:ENTRY_IDX-50,to:EXIT_IDX+15});
  document.getElementById('bc').textContent=String(ENTRY_IDX);
  setTimeout(tick, 800);

  ['tf1','tf4','tfd'].forEach(function(id){
    document.getElementById(id).addEventListener('click',function(){
      document.querySelectorAll('.dbtn').forEach(function(b){b.classList.remove('on')});
      this.classList.add('on');
    });
  });
})();
<\/script>
</body>
</html>`;

export function BacktestDemoChart() {
  const { t, i18n } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const html = useMemo(() => {
    const labels: Record<string, string> = {
      __LANG__: i18n.language,
      __INDICATORS__: t('landing.backtest.demo.indicators'),
      __RUNNING__: t('landing.backtest.demo.running'),
      __RESULT__: t('landing.backtest.demo.result'),
      __PAIR__: t('landing.backtest.demo.pair'),
      __DIRECTION__: t('landing.backtest.demo.direction'),
      __LONG__: t('landing.backtest.demo.long'),
      __ENTRY__: t('landing.backtest.demo.entry'),
      __EXIT__: t('landing.backtest.demo.exit'),
      __PIPS__: t('landing.backtest.demo.pips'),
      __LOTS__: t('landing.backtest.demo.lots'),
      __SESSION__: t('landing.backtest.demo.session'),
      __LONDON__: 'London',
      __CANDLES__: t('landing.backtest.demo.candles'),
      __TRADES__: t('landing.backtest.demo.trades'),
      __WINRATE__: t('landing.backtest.demo.win_rate'),
      __TOTALPNL__: t('landing.backtest.demo.total_pnl'),
    };
    return DEMO_HTML.replace(/__[A-Z]+__/g, token => labels[token] ?? token);
  }, [t, i18n.language]);

  return (
    <div style={{
      width:'100%',height:'480px',borderRadius:'14px',overflow:'hidden',position:'relative',
      boxShadow:'0 8px 60px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.06)',
      background:'#131722',
    }}>
      <iframe
        ref={iframeRef}
        srcDoc={html}
        style={{width:'100%',height:'100%',border:'none',display:'block'}}
        title={t('landing.backtest.demo.alt')}
        sandbox="allow-scripts"
      />
    </div>
  );
}
